import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "modal", "modalDateLbl", "modalDoctorCostLbl", "modalEstimateCostLbl",
    "modalInsertionDate", "modalDoctorCost", "modalEstimateCost", "modalImages",
    "modalSaveBtn", "tooth", "spinner", "lock", "doctorCostField", "doctorProcBtn", "collapse",
    "proceduresAccordion", "oldProceduresCarousel", "noProceduresMessage"
  ]

  connect() {
    console.log('🔄 Insertion Controller v2.2 - FIXED defaultPrice in getProcedureInsurtionImages')
    this.isSaving = false
    this.currentTeethNo = null
    this.isConnected = true
    this.authorizationPollers = new Map()
    this.authorizationDialogUrl = null
    this.authorizationDialogState = null
    this.radiologyModalRefreshTimer = null
    this.boundRefreshOpenRadiologyModal = this.refreshOpenRadiologyModal.bind(this)
    document.addEventListener('notification:displayed', this.boundRefreshOpenRadiologyModal)

    // Initialize autocomplete if needed - keep jQuery for autocomplete for now as migrating it fully is large
    this.initAutocomplete()

    this.element.querySelectorAll('[data-insertion-status-url-param]').forEach((link) => {
      if (this.isPendingWorkflowState(link.dataset.insertionStateParam)) {
        this.startAuthorizationPolling(link.dataset.insertionStatusUrlParam, link)
      }
    })
  }

  disconnect() {
    this.isConnected = false
    this.authorizationPollers.forEach((timer) => window.clearTimeout(timer))
    this.authorizationPollers.clear()
    if (this.radiologyModalRefreshTimer) window.clearTimeout(this.radiologyModalRefreshTimer)
    document.removeEventListener('notification:displayed', this.boundRefreshOpenRadiologyModal)
  }

  showAuthorizationStatus(event) {
    if (event.preventDefault) event.preventDefault()

    const data = event.params || {}
    const link = event.currentTarget
    const statusUrl = data.statusUrl || link.dataset.insertionStatusUrlParam
    if (!statusUrl) return

    $.getJSON(statusUrl).done((response) => {
      if (!response.success) return
      this.applyAuthorizationState(link, response.state, response)
      if (this.shouldOpenLinkedTreatmentModal(link, response.state)) {
        this.openLinkedTreatmentModal(link)
        if (!response.terminal) this.startAuthorizationPolling(statusUrl, link)
        return
      }
      this.showAuthorizationDialog(response.state, statusUrl, response, link)
      if (!response.terminal) this.startAuthorizationPolling(statusUrl, link)
    })
  }

  completeRadiologyProcedure(event) {
    event.preventDefault()
    const data = event.params || {}
    const link = event.currentTarget
    const dispenseUrl = data.dispenseUrl || link.dataset.insertionDispenseUrlParam
    const dispenseState = data.dispenseState || link.dataset.insertionDispenseStateParam
    const resultUrl = data.resultUrl || link.dataset.insertionResultUrlParam
    const resultViewUrl = data.resultViewUrl || link.dataset.insertionResultViewUrlParam
    const resultState = data.resultState || link.dataset.insertionResultStateParam
    const workflowState = data.state || link.dataset.insertionStateParam
    const isArabic = gon.locale === 'ar'
    const ticketPrefill = gon.radiology_ticket_prefill || ''

    if (workflowState === 'waiting_for_authorization') {
      Swal.fire({
        icon: 'info',
        title: isArabic ? 'طلب الأشعة بانتظار التفويض' : 'Radiology request awaiting authorization',
        text: isArabic
          ? 'لا يمكن لمركز الأشعة متابعة الطلب حتى تتم الموافقة ويصدر رقم الطلب.'
          : 'The radiology center cannot continue until authorization is approved and the ticket number is issued.',
        confirmButtonText: isArabic ? 'حسناً' : 'OK'
      })
      return
    }

    if (['radiology_queued', 'radiology_sending', 'radiology_retrying'].includes(workflowState)) {
      Swal.fire({
        icon: 'info',
        title: isArabic ? 'جارٍ تجهيز طلب الأشعة' : 'Radiology request is being processed',
        text: isArabic
          ? 'تمت الموافقة على التفويض، ويجري الآن إرسال طلب الأشعة المنفصل. انتظر حتى يصدر رقم الطلب قبل المتابعة.'
          : 'Authorization is approved and the separate radiology order is being sent. Wait until the ticket number is issued before continuing.',
        confirmButtonText: isArabic ? 'حسناً' : 'OK'
      })
      return
    }

    if (['rejected', 'authorization_failed', 'radiology_failed'].includes(workflowState)) {
      Swal.fire({
        icon: 'error',
        title: isArabic ? 'طلب الأشعة غير متاح' : 'Radiology request unavailable',
        text: isArabic
          ? 'لا يمكن لمركز الأشعة متابعة هذا الطلب. يجب على الطبيب الطالب مراجعة حالة الطلب.'
          : 'The radiology center cannot continue with this request. The ordering doctor must review its status.',
        confirmButtonText: isArabic ? 'حسناً' : 'OK'
      })
      return
    }

    if (resultState === 'sent') {
      this.showSubmittedRadiologyResult(resultViewUrl, isArabic)
      return
    }

    if (['queued', 'sending', 'retrying'].includes(resultState)) {
      Swal.fire({
        icon: 'info',
        title: isArabic ? 'جارٍ إرسال نتائج الأشعة' : 'Radiology results are being sent',
        confirmButtonText: isArabic ? 'حسناً' : 'OK'
      })
      return
    }

    if (resultState === 'pending_dispense') {
      Swal.fire({
        icon: 'info',
        title: isArabic ? 'جارٍ إكمال طلب الأشعة' : 'Completing radiology order',
        text: isArabic
          ? 'تم حفظ الصور والنتائج. سيتم إرسال النتائج تلقائياً بعد قبول طلب الأشعة.'
          : 'The images and findings are saved. The findings will be sent automatically after the radiology order is accepted.',
        confirmButtonText: isArabic ? 'حسناً' : 'OK'
      })
      return
    }

    if (dispenseState === 'sent') {
      Swal.fire({
        title: isArabic ? 'إرسال نتائج الأشعة' : 'Submit radiology results',
        html: `
          <div class="form-group text-left">
            <label for="radiology_findings">${isArabic ? 'نتائج الأشعة' : 'Radiology findings'}</label>
            <textarea id="radiology_findings" class="form-control" rows="6" maxlength="5000"></textarea>
          </div>
          <p class="small text-muted mb-0">${isArabic ? 'سيتم إرسال النتائج النصية وصور ما قبل الإجراء وما بعده إلى خدمة التأمين.' : 'The text findings and before/after images will be sent to the insurance service.'}</p>`,
        showCancelButton: true,
        confirmButtonText: isArabic ? 'إرسال النتائج' : 'Submit results',
        cancelButtonText: isArabic ? 'إلغاء' : 'Cancel',
        showLoaderOnConfirm: true,
        preConfirm: () => {
          const findings = document.getElementById('radiology_findings').value.trim()
          if (!findings) {
            Swal.showValidationMessage(isArabic ? 'يرجى إدخال نتائج الأشعة' : 'Radiology findings are required')
            return false
          }

          const body = new URLSearchParams({ findings })
          return fetch(resultUrl, {
            method: 'POST',
            headers: {
              'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
            },
            body
          }).then(async (response) => {
            const responseBody = await response.json()
            if (!response.ok || !responseBody.success) throw new Error(responseBody.error || 'Radiology result submission failed')
            return responseBody
          }).catch((error) => Swal.showValidationMessage(error.message))
        },
        allowOutsideClick: () => !Swal.isLoading()
      }).then((result) => {
        if (!result.isConfirmed || !result.value) return
        link.dataset.insertionResultStateParam = result.value.state
        Swal.fire({
          icon: 'success',
          title: isArabic ? 'تم حفظ النتائج' : 'Results saved',
          text: result.value.message,
          confirmButtonText: isArabic ? 'حسناً' : 'OK'
        })
      })
      return
    }

    if (['queued', 'sending', 'retrying'].includes(dispenseState)) {
      Swal.fire({
        icon: 'info',
        title: isArabic ? 'جارٍ إرسال قبول طلب الأشعة' : 'Radiology acceptance is being sent',
        confirmButtonText: isArabic ? 'حسناً' : 'OK'
      })
      return
    }

    Swal.fire({
      title: isArabic ? 'إكمال طلب الأشعة' : 'Complete radiology order',
      html: `
        <div class="form-group text-left">
          <label for="radiology_reference_number">${isArabic ? 'رقم الطلب' : 'Ticket number'}</label>
          <input id="radiology_reference_number" class="form-control" type="text" autocomplete="off" inputmode="numeric" value="${this.escapeHtml(ticketPrefill)}">
        </div>
        <div class="form-group text-left">
          <label for="radiology_before_file">${isArabic ? 'صورة ما قبل الإجراء' : 'Before procedure image'}</label>
          <input id="radiology_before_file" class="form-control" type="file" accept="image/*">
        </div>
        <div class="form-group text-left">
          <label for="radiology_after_file">${isArabic ? 'صورة ما بعد الإجراء' : 'After procedure image'}</label>
          <input id="radiology_after_file" class="form-control" type="file" accept="image/*">
        </div>
        <div class="form-group text-left">
          <label for="radiology_findings">${isArabic ? 'نتائج الأشعة' : 'Radiology findings'}</label>
          <textarea id="radiology_findings" class="form-control" rows="6" maxlength="5000"></textarea>
        </div>`,
      didOpen: () => {
        if (!ticketPrefill) return
        const ticketInput = document.getElementById('radiology_reference_number')
        if (ticketInput) ticketInput.select()
      },
      showCancelButton: true,
      confirmButtonText: isArabic ? 'حفظ وإرسال' : 'Save and submit',
      cancelButtonText: isArabic ? 'إلغاء' : 'Cancel',
      showLoaderOnConfirm: true,
      preConfirm: () => {
        const referenceNumber = document.getElementById('radiology_reference_number').value.trim()
        const before = document.getElementById('radiology_before_file').files[0]
        const after = document.getElementById('radiology_after_file').files[0]
        const findings = document.getElementById('radiology_findings').value.trim()
        if (!referenceNumber) {
          Swal.showValidationMessage(isArabic ? 'يرجى إدخال رقم الطلب' : 'Ticket number is required')
          return false
        }
        if (!before || !after) {
          Swal.showValidationMessage(isArabic ? 'يرجى رفع صورتي ما قبل وما بعد الإجراء' : 'Both before and after procedure images are required')
          return false
        }
        if (!findings) {
          Swal.showValidationMessage(isArabic ? 'يرجى إدخال نتائج الأشعة' : 'Radiology findings are required')
          return false
        }

        const formData = new FormData()
        formData.append('reference_number', referenceNumber)
        formData.append('first_file', before)
        formData.append('second_file', after)
        formData.append('findings', findings)
        return fetch(dispenseUrl, {
          method: 'POST',
          headers: { 'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content') },
          body: formData
        }).then(async (response) => {
          const body = await response.json()
          if (!response.ok || !body.success) throw new Error(body.error || 'Radiology submission failed')
          return body
        }).catch((error) => Swal.showValidationMessage(error.message))
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then((result) => {
      if (!result.isConfirmed || !result.value) return
      link.dataset.insertionDispenseStateParam = result.value.state
      link.dataset.insertionResultStateParam = result.value.result_state
      Swal.fire({
        icon: 'success',
        title: isArabic ? 'تم حفظ طلب الأشعة' : 'Radiology order saved',
        text: result.value.message,
        confirmButtonText: isArabic ? 'حسناً' : 'OK'
      })
    })
  }

  showSubmittedRadiologyResult(resultViewUrl, isArabic) {
    if (!resultViewUrl) return

    Swal.fire({
      title: isArabic ? 'جارٍ تحميل نتائج الأشعة' : 'Loading radiology results',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading()
    })

    fetch(resultViewUrl, { headers: { 'Accept': 'application/json' } })
      .then(async (response) => {
        const body = await response.json()
        if (!response.ok || !body.success) throw new Error(body.error || 'Unable to load radiology results')
        return body
      })
      .then((body) => {
        const findings = this.escapeHtml(body.findings || '')
        const images = Array.isArray(body.images) ? body.images : []
        const imagesHtml = images.map((item) => `
          <div class="col-md-6 mb-3">
            <div class="card h-100">
              <a href="${this.escapeHtml(item.url)}" target="_blank" rel="noopener noreferrer">
                <img src="${this.escapeHtml(item.url)}" alt="${this.escapeHtml(item.title)}" class="card-img-top" style="height: 280px; object-fit: contain; background: #f8f9fa;">
              </a>
              <div class="card-header"><h5 class="card-title mb-0">${this.escapeHtml(item.title)}</h5></div>
            </div>
          </div>`).join('')
        Swal.fire({
          icon: 'success',
          title: isArabic ? 'نتائج الأشعة النهائية' : 'Final radiology results',
          html: `
            <div class="${isArabic ? 'text-right' : 'text-left'}" dir="${isArabic ? 'rtl' : 'ltr'}">
              <label class="font-weight-bold">${isArabic ? 'نتائج الأشعة' : 'Radiology findings'}</label>
              <div class="form-control bg-light" style="height: auto; min-height: 100px; white-space: pre-wrap;">${findings}</div>
              ${imagesHtml ? `<div class="row mt-3">${imagesHtml}</div>` : ''}
            </div>`,
          width: images.length > 0 ? '900px' : undefined,
          confirmButtonText: isArabic ? 'إغلاق' : 'Close'
        })
      })
      .catch((error) => Swal.fire({
        icon: 'error',
        title: isArabic ? 'تعذر تحميل النتائج' : 'Unable to load results',
        text: error.message,
        confirmButtonText: isArabic ? 'حسناً' : 'OK'
      }))
  }

  showAuthorizationDialog(state, statusUrl, response = {}, link = null) {
    const isArabic = gon.locale === 'ar'

    if (this.isPendingWorkflowState(state)) {
      this.authorizationDialogUrl = statusUrl
      this.authorizationDialogState = state
      Swal.fire({
        title: response.title || (isArabic ? 'بانتظار التفويض' : 'Waiting for authorization'),
        html: `<div class="spinner-border text-info mb-3" role="status" aria-hidden="true"></div><p>${response.message || (isArabic ? 'تم إرسال طلب التفويض. يرجى الانتظار حتى وصول القرار.' : 'The authorization request was sent. Please wait for the decision.')}</p>`,
        showConfirmButton: true,
        confirmButtonText: isArabic ? 'إغلاق ومتابعة العمل' : 'Close and continue working',
        allowOutsideClick: true,
        allowEscapeKey: true,
        didClose: () => {
          if (this.authorizationDialogUrl === statusUrl) this.authorizationDialogUrl = null
          if (this.authorizationDialogState === state) this.authorizationDialogState = null
        }
      })
      return
    }

    if (state === 'radiology_results_available') {
      const allowNewRadiologyRequest = Boolean(link) && this.isDoctorInsertionWorkflow()
      Swal.fire({
        icon: 'success',
        title: response.title || (isArabic ? 'نتائج الأشعة متوفرة' : 'Radiology results available'),
        text: response.message,
        confirmButtonText: isArabic ? 'عرض النتائج' : 'View results',
        showDenyButton: Boolean(response.print_url),
        denyButtonText: isArabic ? 'طباعة الطلب' : 'Print request',
        footer: allowNewRadiologyRequest
          ? `<button type="button" id="new_radiology_request_btn" class="btn btn-success">${isArabic ? 'إرسال طلب أشعة جديد' : 'New Radiology Request'}</button>`
          : undefined,
        didOpen: () => {
          if (!allowNewRadiologyRequest) return
          document.getElementById('new_radiology_request_btn')?.addEventListener('click', () => {
            Swal.close()
            window.setTimeout(() => this.openCompletedRadiologyForRepeat(link), 0)
          })
        },
        showLoaderOnConfirm: true,
        preConfirm: () => fetch(response.result_url, { headers: { 'Accept': 'application/json' } })
          .then(async (resultResponse) => {
            const body = await resultResponse.json()
            if (!resultResponse.ok || !body.success) throw new Error(body.error || 'Unable to load radiology results')
            return body
          })
          .catch((error) => Swal.showValidationMessage(error.message)),
        allowOutsideClick: () => !Swal.isLoading()
      }).then((result) => {
        if (result.isDenied && response.print_url) {
          window.open(response.print_url, '_blank', 'noopener,noreferrer')
        } else if (result.isConfirmed && result.value) {
          const findings = this.escapeHtml(result.value.findings || '')
          const images = Array.isArray(result.value.images) ? result.value.images : []
          const imagesHtml = images.length > 0 ? `
            <div class="row mt-3">
              ${images.map((item) => `
                <div class="col-md-6 mb-3">
                  <div class="card h-100">
                    <a href="${this.escapeHtml(item.url)}" target="_blank" rel="noopener noreferrer">
                      <img src="${this.escapeHtml(item.url)}" alt="${this.escapeHtml(item.title)}" class="card-img-top" style="height: 280px; object-fit: contain; background: #f8f9fa;">
                    </a>
                    <div class="card-header"><h5 class="card-title mb-0">${this.escapeHtml(item.title)}</h5></div>
                  </div>
                </div>`).join('')}
            </div>` : ''
          const warningHtml = result.value.warning ? `<div class="alert alert-warning mt-3 mb-0">${this.escapeHtml(result.value.warning)}</div>` : ''
          Swal.fire({
            icon: result.value.warning ? 'warning' : 'info',
            title: isArabic ? 'نتائج الأشعة' : 'Radiology results',
            html: `<div class="text-left"><p style="white-space: pre-wrap;">${findings}</p>${imagesHtml}${warningHtml}</div>`,
            width: images.length > 0 ? '900px' : undefined,
            confirmButtonText: isArabic ? 'حسناً' : 'OK'
          })
        }
      })
      return
    }

    if (state === 'radiology_sent' || ['radiology_result_sending', 'radiology_result_retrying'].includes(state)) {
      Swal.fire({
        icon: 'info',
        title: response.title || (isArabic ? 'بانتظار نتائج الأشعة' : 'Waiting for radiology results'),
        text: `${isArabic ? 'رقم الطلب' : 'Ticket number'}: ${response.radiology_reference_number || '-'}\n\n${response.message || (isArabic ? 'تم إرسال الطلب إلى مركز الأشعة. ستبقى هذه الحالة بانتظار إضافة النتائج.' : 'The request was sent to the radiology center. This procedure will remain waiting until results are added.')}`,
        confirmButtonText: isArabic ? 'حسناً' : 'OK',
        showDenyButton: Boolean(response.print_url),
        denyButtonText: isArabic ? 'طباعة الطلب' : 'Print request',
        denyButtonColor: '#0d6efd'
      }).then((result) => {
        if (result.isDenied && response.print_url) {
          window.open(response.print_url, '_blank', 'noopener,noreferrer')
        }
      })
      return
    }

    if (state === 'radiology_failed' && response.retryable && response.retry_url) {
      Swal.fire({
        icon: 'error',
        title: response.title,
        text: response.error || response.message,
        showCancelButton: true,
        confirmButtonText: isArabic ? 'إعادة المحاولة' : 'Retry',
        cancelButtonText: isArabic ? 'إغلاق' : 'Close'
      }).then((result) => {
        if (!result.isConfirmed) return
        $.ajax({
          type: 'post',
          url: response.retry_url,
          dataType: 'json',
          headers: { 'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content') },
          success: (retryResponse) => {
            if (!retryResponse.success) return
            this.applyAuthorizationState(link, retryResponse.state)
            this.showAuthorizationDialog(retryResponse.state, statusUrl)
            this.startAuthorizationPolling(statusUrl, link)
          },
          error: (xhr) => Swal.fire({ icon: 'error', text: xhr.responseJSON?.error || 'Retry failed' })
        })
      })
      return
    }

    Swal.fire({
      icon: state === 'approved' ? 'success' : 'error',
      title: response.title || (state === 'approved'
        ? (isArabic ? 'تمت الموافقة على التفويض' : 'Authorization approved')
        : (isArabic ? 'تم رفض التفويض' : 'Authorization rejected')),
      text: response.message || (state === 'approved'
        ? (isArabic ? 'يمكنك الآن متابعة الإجراء.' : 'You can now continue with the procedure.')
        : (isArabic ? 'لم تتم الموافقة على هذا الإجراء.' : 'This procedure was not authorized.')),
      confirmButtonText: isArabic ? 'حسناً' : 'OK'
    })
  }

  openCompletedRadiologyForRepeat(link) {
    if (!link) return
    const d = link.dataset
    const procedureCode = d.insertionProcIdParam
    const teethPosition = d.insertionTeethPosParam
    const isBlocked = d.insertionIsBlockedParam === 'true'
    const isAttached = d.insertionIsAttachedParam
    const labels = {
      date: d.insertionDateLblParam || gon.insertion_date,
      doctorCost: d.insertionDoctorCostLblParam || gon.one_teeth_require,
      estimateCost: d.insertionEstimateCostLblParam || gon.estimate_cost
    }
    const addRequest = () => this.addNewInsertion(
      procedureCode,
      teethPosition,
      labels,
      d.insertionCoverageRateParam,
      d.insertionDefaultPriceParam,
      isBlocked,
      isAttached,
      true
    )

    if (!isBlocked) {
      addRequest()
      return
    }

    this.fetchApprovalStatus(procedureCode, teethPosition, (approval) => {
      if (!approval || (approval.approval_status === true && approval.is_proceeded === true)) {
        this.requestApprovalForNewInsertion(procedureCode, teethPosition, null)
      } else if (approval.approval_status === false && approval.is_proceeded === false) {
        Swal.fire({
          icon: 'info',
          title: gon.locale === 'ar' ? 'في انتظار الموافقة' : 'Waiting for Approval',
          text: gon.locale === 'ar' ? 'طلبك قيد المراجعة.' : 'Your request is under review.'
        })
      } else if (approval.approval_status === false && approval.is_proceeded === true) {
        Swal.fire({
          icon: 'error',
          title: gon.locale === 'ar' ? 'تم رفض الطلب' : 'Request Rejected',
          text: gon.locale === 'ar' ? 'تم رفض طلب الموافقة الخاص بك.' : 'Your approval request has been rejected.'
        })
      } else if (approval.approval_status === true && approval.is_proceeded === false) {
        addRequest()
      }
    })
  }

  escapeHtml(value) {
    const element = document.createElement('div')
    element.textContent = String(value == null ? '' : value)
    return element.innerHTML
  }

  startAuthorizationPolling(statusUrl, link, options = {}) {
    if (!statusUrl || this.authorizationPollers.has(statusUrl)) return
    let authorizationApprovalShown = false

    const poll = () => {
      $.ajax({
        type: 'get',
        url: statusUrl,
        dataType: 'json',
        success: (response) => {
          if (!response.success) return this.scheduleAuthorizationPoll(statusUrl, poll)

          const authorizationJustApproved = response.authorization_state === 'approved' &&
            !authorizationApprovalShown && typeof options.onAuthorizationApproved === 'function'
          this.applyAuthorizationState(link, response.state, response)
          const linkedTreatmentWorkflow = this.shouldOpenLinkedTreatmentModal(link, response.state)
          if (linkedTreatmentWorkflow && this.authorizationDialogUrl === statusUrl && Swal.isVisible()) {
            this.openLinkedTreatmentModal(link)
          }
          if (authorizationJustApproved) {
            authorizationApprovalShown = true
            options.onAuthorizationApproved(response)
          }
          if (!response.terminal) return this.scheduleAuthorizationPoll(statusUrl, poll)

          this.authorizationPollers.delete(statusUrl)
          const terminalFailure = ['rejected', 'authorization_failed', 'radiology_failed', 'radiology_result_failed'].includes(response.state)
          const radiologyOrderReady = ['radiology_sent', 'radiology_results_available'].includes(response.state)
          if (terminalFailure ||
              (radiologyOrderReady && !linkedTreatmentWorkflow) ||
              (!linkedTreatmentWorkflow && this.authorizationDialogUrl === statusUrl && Swal.isVisible() && !authorizationApprovalShown)) {
            this.authorizationDialogUrl = null
            this.showAuthorizationDialog(response.state, statusUrl, response)
          }
          if ((response.state === 'approved' || response.state === 'radiology_sent') && typeof options.onApproved === 'function') options.onApproved()
        },
        error: () => this.scheduleAuthorizationPoll(statusUrl, poll)
      })
    }

    this.scheduleAuthorizationPoll(statusUrl, poll, 1200)
  }

  scheduleAuthorizationPoll(statusUrl, callback, delay = 3000) {
    if (!this.isConnected) return
    const oldTimer = this.authorizationPollers.get(statusUrl)
    if (oldTimer) window.clearTimeout(oldTimer)
    this.authorizationPollers.set(statusUrl, window.setTimeout(callback, delay))
  }

  isPendingWorkflowState(state) {
    return ['waiting_for_authorization', 'waiting_for_radiology_authorization', 'waiting_for_claim', 'radiology_queued', 'radiology_sending', 'radiology_retrying', 'radiology_result_sending', 'radiology_result_retrying'].includes(state)
  }

  isDoctorInsertionWorkflow() {
    return gon.user_type === 'doctor' || gon.user_type === 'admin'
  }

  isHakeemInsertionWorkflow() {
    return this.isDoctorInsertionWorkflow() && gon.tpo_integration_enabled === true
  }

  linkedRadiologyStatusFor(link) {
    if (!link || !this.isHakeemInsertionWorkflow()) return null
    const procedureCode = link.dataset.insertionProcIdParam
    const teethPosition = link.dataset.insertionTeethPosParam
    return Array.from(document.querySelectorAll('.linked-radiology-status-data')).find((element) =>
      element.dataset.procedureCode === String(procedureCode) &&
      element.dataset.toothPosition === String(teethPosition)
    )
  }

  shouldOpenLinkedTreatmentModal(link, state) {
    const linkedWorkflowStates = [
      'waiting_for_radiology_authorization',
      'radiology_queued',
      'radiology_sending',
      'radiology_retrying',
      'radiology_sent',
      'radiology_result_sending',
      'radiology_result_retrying',
      'radiology_results_available'
    ]
    return linkedWorkflowStates.includes(state) && Boolean(this.linkedRadiologyStatusFor(link))
  }

  openLinkedTreatmentModal(link) {
    this.authorizationDialogUrl = null
    this.authorizationDialogState = null
    if (Swal.isVisible()) Swal.close()

    // The insertion modal is normally still underneath the authorization
    // dialog. Keep it open and update its linked radiology controls in place.
    if ($('#procedureEditModal').hasClass('show')) {
      this.refreshOpenRadiologyModal()
      return
    }

    const data = link.dataset
    window.setTimeout(() => this.deleteConfirmedProcedure({
      preventDefault: () => {},
      params: {
        insurtionId: data.insertionInsurtionIdParam,
        procId: data.insertionProcIdParam,
        teethPos: data.insertionTeethPosParam,
        isAttached: data.insertionIsAttachedParam,
        insertionDate: data.insertionInsertionDateParam,
        doctorCost: data.insertionDoctorCostParam,
        estimateCost: data.insertionEstimateCostParam,
        coverageRate: data.insertionCoverageRateParam,
        defaultPrice: data.insertionDefaultPriceParam,
        isBlocked: data.insertionIsBlockedParam,
        dateLbl: data.insertionDateLblParam,
        doctorCostLbl: data.insertionDoctorCostLblParam,
        estimateCostLbl: data.insertionEstimateCostLblParam
      }
    }), 0)
  }

  toothStatusLabel(status) {
    const labels = gon.locale === 'ar'
      ? { locked: 'مقفل', waiting: 'قيد الانتظار' }
      : { locked: 'Locked', waiting: 'Waiting' }
    return labels[status] || ''
  }

  toothStatusDot(status) {
    return `<i class="doctor-legend-dot is-${status}" aria-hidden="true"></i>`
  }

  renderToothStatusMarker(marker, status) {
    if (!marker) return
    const label = this.toothStatusLabel(status)
    if (label) {
      marker.setAttribute('aria-label', label)
      marker.title = label
    }
    marker.innerHTML = this.toothStatusDot(status)
  }

  applyAuthorizationState(link, state, workflow = {}) {
    if (!link) return

    // Once the treatment itself has a workflow state, its marker is the
    // primary tooth status. Keep linked radiology details in the modal, but
    // do not stack a second badge in the same position.
    link.closest('.item')?.querySelector('.linked-radiology-tooth-status')?.remove()

    link.dataset.insertionStateParam = state
    if (typeof workflow.is_blocked === 'boolean') {
      link.dataset.insertionIsBlockedParam = String(workflow.is_blocked)
    }
    link.classList.remove('tooth-authorization-pending', 'tooth-authorization-rejected', 'tooth-authorization-sent')
    const image = link.querySelector('img')
    let marker = link.querySelector('.tooth-authorization-marker')

    if (['radiology_sent', 'radiology_results_available'].includes(state)) {
      if (image) {
        image.classList.add('color')
        image.classList.remove('black-white')
      }
      link.classList.add('tooth-authorization-state', 'tooth-authorization-sent')
      link.setAttribute('data-action', this.shouldOpenLinkedTreatmentModal(link, state)
        ? 'click->insertion#deleteConfirmedProcedure'
        : 'click->insertion#showAuthorizationStatus')
      if (!marker) {
        marker = document.createElement('span')
        marker.className = 'tooth-authorization-marker doctor-tooth-status-marker'
        marker.setAttribute('role', 'status')
        link.appendChild(marker)
      }
      marker.innerHTML = this.toothStatusDot(state === 'radiology_results_available' ? 'results' : 'radiology')
      this.refreshApprovedToothCount(link)
      return
    }

    if (state === 'approved' || state === 'not_required') {
      if (image) {
        image.classList.add('color')
        image.classList.remove('black-white')
      }
      if (marker) marker.remove()
      link.classList.remove('tooth-authorization-state')
      link.setAttribute('data-action', 'click->insertion#deleteConfirmedProcedure')
      this.restoreProcedureLock(link)
      this.refreshApprovedToothCount(link)
      return
    }

    if (state === 'waiting_for_radiology_authorization') {
      if (image) {
        image.classList.add('color')
        image.classList.remove('black-white', 'color-fake')
      }
      link.classList.add('tooth-authorization-state', 'tooth-authorization-pending')
      link.setAttribute('data-action', 'click->insertion#deleteConfirmedProcedure')
      if (!marker) {
        marker = document.createElement('span')
        marker.className = 'tooth-authorization-marker doctor-tooth-status-marker'
        marker.setAttribute('role', 'status')
        link.appendChild(marker)
      }
      marker.innerHTML = this.toothStatusDot('waiting')
      this.refreshApprovedToothCount(link)
      return
    }

    if (image) {
      image.classList.add('black-white')
      image.classList.remove('color', 'color-fake')
    }
    link.classList.add('tooth-authorization-state')
    const failed = ['rejected', 'claim_partially_approved', 'claim_rejected', 'authorization_failed', 'radiology_failed', 'radiology_result_failed'].includes(state)
    link.classList.add(failed ? 'tooth-authorization-rejected' : 'tooth-authorization-pending')
    link.setAttribute('data-action', this.shouldOpenLinkedTreatmentModal(link, state)
      ? 'click->insertion#deleteConfirmedProcedure'
      : 'click->insertion#showAuthorizationStatus')

    if (!marker) {
      marker = document.createElement('span')
      marker.className = 'tooth-authorization-marker doctor-tooth-status-marker'
      marker.setAttribute('role', 'status')
      link.appendChild(marker)
    }
    marker.innerHTML = this.toothStatusDot(failed ? 'failed' : 'waiting')
  }

  restoreProcedureLock(link) {
    if (link.dataset.insertionIsBlockedParam !== 'true') return

    let lock = link.querySelector('[class*="lock-proc-"]')
    if (!lock) {
      const toothKey = link.dataset.insertionNoParam ||
        Array.from(link.classList).find((name) => /^link_\d+_.+/.test(name))?.replace(/^link_/, '')
      if (!toothKey) return

      lock = document.createElement('span')
      lock.className = `mr-2 lock-proc-${toothKey}`
      lock.setAttribute('role', 'status')
      lock.style.cssText = 'position: absolute; top: 4%; left: 2%; margin: auto;'
      link.appendChild(lock)
    }

    lock.style.display = ''
    this.renderToothStatusMarker(lock, 'locked')
  }

  refreshApprovedToothCount(link) {
    const linkClass = Array.from(link.classList).find((name) => /^link_\d+_.+/.test(name))
    if (!linkClass) return

    const match = linkClass.match(/^link_\d+_(.+)$/)
    if (!match) return

    const procedureCode = match[1]
    const collapse = document.getElementById(`collapse_${procedureCode}`)
    const button = document.getElementById(`doctor_proc_${procedureCode}`)
    if (!collapse || !button) return

    const count = collapse.querySelectorAll('.color').length
    button.value = button.value.match(/\(\d+\)$/)
      ? button.value.replace(/\(\d+\)$/, `(${count})`)
      : `${button.value} (${count})`
    button.disabled = false
  }

  initAutocomplete() {
    if ($("#procedures_insurtions_tags").length > 0) {
      $("#procedures_insurtions_tags").autocomplete({
        source: (request, response) => {
          $.ajax({
            url: "/admin/doctors/autocomplete",
            dataType: 'json',
            data: { query: request.term, insured_number: gon.insured_number, locale: gon.locale, contract_code: gon.contract_code },
            success: (data) => {
              response($.map(data, (value, key) => {
                return { label: value.label, value: value.value, key: value.key };
              }));
            }
          });
        },
        select: (event, ui) => {
          event.target.value = ui.item.label;
          $('#procedures_insurtions_doctor_email').val(ui.item.key);
          $('#procedures_insurtions_doctor_name').val(ui.item.label);

          if (this.hasProceduresAccordionTarget) {
            this.proceduresAccordionTarget.style.display = "inline"
          } else {
            $('#procedures_accordion').css("display", "inline");
          }

          $('#doctor_email').val(gon.doctor_id);
          $('#doctor_name').val(gon.doctor_name);
          $('#locale').val(gon.locale);
          $('#insured_number').val(gon.insured_number);

          return false;
        }
      });
    }
  }

  selectTeeth(event) {
    if (event.preventDefault) event.preventDefault()

    const data = event.params || {}
    // Handle direct call from checkApprovalStatus
    console.log('selectTeeth data:', data)
    const no = data.no
    const procId = data.procId
    const isAttached = data.isAttached

    const toothElement = event.currentTarget
    const isRadiology = toothElement.closest('.procedure-item')?.dataset.isRadiology === 'true'
    const coloredElement = toothElement.querySelector('.color') || toothElement.querySelector('.black-white')

    // If called from mock event, coloredElement might be different or already updated
    const isColored = coloredElement ? coloredElement.classList.contains('color') : false

    // Check if locked
    const lockIcon = toothElement.querySelector(`.lock-proc-${no}`)
    const isLocked = gon.user_type === 'doctor' && lockIcon && lockIcon.style.display !== 'none' && !isColored

    const idsData = no.split("_")

    if (this.isHakeemInsertionWorkflow() && isRadiology && (procId === -1 || procId === '-1') && !data.radiologyApprovalChecked) {
      this.fetchApprovalStatus(idsData[1], idsData[0], (approval) => {
        if (approval && approval.approval_status === true && approval.is_proceeded === false && approval.hakeem_approved === true && approval.requested_for_procedure_code) {
          this.selectTeeth({
            preventDefault: () => {},
            currentTarget: toothElement,
            params: Object.assign({}, data, { radiologyApprovalChecked: true })
          })
        } else {
          Swal.fire({
            icon: 'warning',
            title: gon.locale === 'ar' ? 'موافقة الأشعة مطلوبة' : 'Radiology approval required',
            text: gon.locale === 'ar'
              ? 'اطلب إجراء الأشعة من نافذة الإجراء العلاجي الذي يحتاج إلى أشعة، ثم انتظر الموافقة.'
              : 'Request this radiology procedure from the treatment that needs radiology, then wait for approval.'
          })
        }
      })
      return
    }

    // Always check for history first
    this.checkAndShowHistoryTab(idsData[1], idsData[0], (showHistoryTab, historyData) => {
      // Prepare labels
      const labels = {
        date: data.dateLbl || (gon && gon.insertion_date) || 'Insertion date',
        estimateCost: data.estimateCostLbl || (gon && gon.estimate_cost) || 'Estimate cost',
        cancel: data.cancelLbl || (gon && gon.cancel) || 'Cancel',
        confirm: data.confirmLbl || (gon && gon.save_label) || 'Save',
        doctorCost: data.doctorCostLbl || (gon && gon.one_teeth_require) || 'Doctor cost',
        errMsg: data.errMsg || (gon && gon.empty_fields) || 'Please fill all the required fields'
      }

      const defaultPrice = parseFloat(data.defaultPrice) || 0
      const coverageRate = parseFloat(data.coverageRate) || 0

      // Auto-fill estimate cost with default price if not set
      const validDefaultPrice = !isNaN(defaultPrice) ? defaultPrice : ''
      const estimateCost = data.estimateCost || validDefaultPrice

      let doctorCost = data.doctorCost || validDefaultPrice
      const oldProc = data.oldProc || ["---"]
      const history = data.history || ""
      const insertionDate = data.insertionDate || ''
      const isBlocked = data.isBlocked || false

      if (showHistoryTab) {
        // History exists or blocked+approved -> Show modal with history
        if (!this.isSaving) {
          this.isSaving = true

          const handleSave = (result) => {
            this.isSaving = false
            this.addInsuredProcedure(idsData, procId, result)
          }

          // First time clicking this tooth - track it and show loading
          this.currentTeethNo = no

          // Show spinner
          const spinner = document.querySelector(`.spinner-proc-${no}`)
          if (spinner) spinner.style.display = "inline"

          // Auto-fill for doctor
          if (gon.user_type === 'doctor') {
            doctorCost = defaultPrice
          }

          // Check if images are required and we have a valid procedure ID
          const requiresImages = (isAttached === true || isAttached === 'true')
          // For new insertions, use today's date from server
          const historyInsertionDate = insertionDate || gon.today_date || ''
          if (requiresImages && procId != -1 && procId != "-1") {
            this.getProcedureInsurtionImages(no, procId, labels, historyInsertionDate, doctorCost, estimateCost, coverageRate, defaultPrice, isBlocked, requiresImages, true, historyData)
          } else {
            this.setInsuredProcedure(no, procId, labels, oldProc, history, doctorCost, doctorCost, coverageRate, defaultPrice, historyInsertionDate, isAttached, handleSave, { showHistoryTab: true, historyData: historyData }, isRadiology)
          }
        }
      } else {
        if (isLocked) {
          // Locked (e.g. globally all-blocked tooth) -> allow requesting approval for NEW insertion.
          // Show approval-request swal with the reason (blocking procedure) if available.
          const idsData = no.split("_")
          const blockerName = data.blockedByProcName
          const blockerCode = data.blockedByProcCode

          const reasonHtml =
            blockerName
              ? (gon.locale === 'ar'
                ? `هذا السن مغلق بسبب الإجراء: <b>${blockerName}</b> (${blockerCode || ''}) والذي تم ضبطه على منع جميع الإجراءات على نفس السن.`
                : `This tooth is locked because of: <b>${blockerName}</b> (${blockerCode || ''}), which is configured to block all procedures on this tooth.`)
              : (gon.locale === 'ar'
                ? 'هذا السن مغلق بسبب إجراء سابق تم ضبطه على منع جميع الإجراءات على نفس السن.'
                : 'This tooth is locked because a previous procedure is configured to block all procedures on this tooth.')

          Swal.fire({
            title: gon.approval_new_title || (gon.locale === 'ar' ? 'يتطلب الحصول على موافقة' : 'Approval required'),
            html: `${reasonHtml}<hr/>${gon.approval_new_msg || ''}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            confirmButtonText: gon.approval_new_confirm || (gon.locale === 'ar' ? 'نعم، أرسل الطلب' : 'Yes, send request'),
            cancelButtonText: gon.approval_new_cancel || (gon.locale === 'ar' ? 'إلغاء' : 'Cancel')
          }).then((result) => {
            if (result.isConfirmed) {
              this.sendProceduralApproval(idsData[1], idsData[0])
            }
          })
        } else {
          // Unlocked and no history -> Show normal modal
          if (!this.isSaving) {
            this.isSaving = true

            const handleSave = (result) => {
              this.isSaving = false
              this.addInsuredProcedure(idsData, procId, result)
            }

            if (!isColored) {
              // First time clicking this tooth - track it and show loading
              this.currentTeethNo = no

              // Show spinner
              const spinner = document.querySelector(`.spinner-proc-${no}`)
              if (spinner) spinner.style.display = "inline"

              // Auto-fill for doctor
              if (gon.user_type === 'doctor' || gon.user_type === 'admin') {
                doctorCost = defaultPrice
              }

              // For new insertions, use today's date from server
              const newInsertionDate = insertionDate || gon.today_date || ''

              this.setInsuredProcedure(no, procId, labels, oldProc, history, doctorCost, doctorCost, coverageRate, defaultPrice, newInsertionDate, isAttached, handleSave, { showHistoryTab: false }, isRadiology)
            } else {
              // Already colored (editing existing?) - usually this goes to deleteConfirmedProcedure but just in case
              this.setInsuredProcedure(no, procId, labels, oldProc, history, doctorCost, estimateCost, coverageRate, defaultPrice, insertionDate, isAttached, handleSave, { showHistoryTab: false }, isRadiology)
            }
          }
        }
      }
    })
  }

  setInsuredProcedure(id, procId, labels, oldProc, history, doctorCost, estimateCost, coverageRate, defaultPrice, insertionDate, isAttached = false, handleSaveOverride = null, preCheckResult = null, isRadiology = false) {
    const idsData = id.split("_")
    const teethPos = idsData[0]
    const procCode = idsData[1]

    console.log('Extracted from id:', { teethPos, procCode, idsData })

    // Check if this is a new insertion (procId is -1 or "-1")
    const isNewInsertion = (procId === -1 || procId === "-1")

    // For new insertions, use today's date from server
    if (isNewInsertion && !insertionDate) {
      insertionDate = gon.today_date || ''
    }

    // Check if procedure requires images
    const requiresImages = (isAttached === true || isAttached === 'true')
    const resolvedIsRadiology = isRadiology === true || this.isRadiologyProcedure(procCode)
    const procedureItem = Array.from(document.querySelectorAll('.procedure-item')).find((item) => item.dataset.procedureCode === procCode)
    const needsRadiology = procedureItem?.dataset.needsRadiology === 'true'

    // For new procedures with images, show a placeholder upload button
    let imagesHtml = ''
    if (requiresImages && !resolvedIsRadiology) {
      imagesHtml = `<div class="alert alert-info text-center">
        <i class="fe fe-info mr-2"></i>
        ${gon.locale === 'ar' ? 'سيتم تفعيل رفع الصور بعد حفظ الإجراء' : 'Image upload will be available after saving the procedure'}
      </div>`
    }

    const openModalCallback = (showHistoryTab, historyData) => {
      console.log('=== checkAndShowHistoryTab CALLBACK ===')
      console.log('showHistoryTab:', showHistoryTab)
      console.log('historyData:', historyData)
      this.showProcedureModal({
        dateLabel: labels.date || (gon && gon.insertion_date),
        doctorCostLabel: labels.doctorCost || (gon && gon.one_teeth_require),
        estimateCostLabel: labels.estimateCost || (gon && gon.estimate_cost),
        insurtionDate: insertionDate || '',
        doctorCost: doctorCost, // Pass the auto-filled cost
        estimateCost: estimateCost,
        imagesHtml: imagesHtml, // Show placeholder for procedures with images
        coverageRate: coverageRate,
        defaultPrice: defaultPrice,
        isBlocked: false, // New procedures are not blocked
        disableAllFields: false,
        showSaveButton: true,
        showHistoryTab: showHistoryTab,
        procedureCode: procCode,
        teethPosition: teethPos,
        historyData: historyData,
        isNewInsertion: isNewInsertion,
        isRadiology: resolvedIsRadiology,
        needsRadiology: needsRadiology,
        onSave: (result) => {
          // Existing insertion: update (do NOT create a new record)
          if (!isNewInsertion) {
            this.isSaving = false
            this.updateInsuredProcedure(procId, result, id)
            return
          }

          const costInput = document.getElementById(`cost_proc_${idsData[1]}`)
          if (costInput) {
            costInput.classList.remove("bg-danger-light")
            costInput.classList.remove("border-danger")
          }

          let estimateCost = 0
          let approvalStatus = false

          if (result.value.estimateCost !== "") {
            estimateCost = result.value.estimateCost
            approvalStatus = true
          }

          $.ajax({
            type: "post",
            url: "/admin/procedures/insertion/add",
            dataType: "json",
            beforeSend: (xhr) => {
              xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content'))
              if (this.isDoctorInsertionWorkflow()) {
                this.showAuthorizationDialog('waiting_for_authorization', `creating-${id}`, {
                  message: gon.locale === 'ar'
                    ? 'جارٍ حفظ الإجراء وإرسال طلب التفويض...'
                    : 'Saving the procedure and sending the authorization request...'
                })
              }
            },
            data: {
              insured_number: gon.insured_number,
              doctor_id: gon.doctor_id,
              doctor_cost: result.value.doctorCost,
              estimate_cost: estimateCost,
              procedure_code: idsData[1],
              status: true,
              teeth_position: idsData[0],
              procedure_id: procId,
              approval_status: approvalStatus,
              insurtion_date: result.value.insurtionDate,
              locale: gon.locale,
              contract_code: gon.contract_code,
              date_of_birth: result.value.dateOfBirth,
              // Only set to true when the user explicitly confirms override.
              force_add: false
            },
            success: (resp) => {
              this.isSaving = false
              const spinner = document.querySelector(`.spinner-proc-${id}`)
              if (spinner) spinner.style.display = "none"

              if (resp.success) {
                if (costInput) costInput.disabled = true

                // Update link attributes
                const link = document.querySelector(`.link_${id}`)
                if (link) {
                  // Set params for deleteConfirmedProcedure
                  link.dataset.insertionInsurtionIdParam = resp.id
                  link.dataset.insertionProcIdParam = idsData[1] // Procedure Code
                  link.dataset.insertionTeethPosParam = idsData[0] // Teeth Pos

                  link.dataset.insertionIsAttachedParam = resp.is_with_image
                  link.dataset.insertionInsertionDateParam = resp.insurtion_date
                  link.dataset.insertionDoctorCostParam = resp.doctor_cost
                  link.dataset.insertionEstimateCostParam = resp.estimate_cost
                  link.dataset.insertionCoverageRateParam = coverageRate
                  link.dataset.insertionDefaultPriceParam = defaultPrice
                  link.dataset.insertionIsBlockedParam = resp.is_blocked
                  link.dataset.insertionStatusUrlParam = resp.authorization_status_url
                  link.dataset.insertionStateParam = resp.authorization_state
                  link.setAttribute(
                    'data-action',
                    this.isPendingWorkflowState(resp.authorization_state)
                      ? 'click->insertion#showAuthorizationStatus'
                      : 'click->insertion#deleteConfirmedProcedure'
                  )
                  this.applyAuthorizationState(link, resp.authorization_state, resp)
                }


                const docProcBtn = document.getElementById(`doctor_proc_${resp.procedure_id}`)
                const collapse = document.getElementById(`collapse_${idsData[1]}`)

                if (docProcBtn && collapse) {
                  const count = collapse.querySelectorAll('.color').length
                  docProcBtn.value = `${resp.label} (${count})`
                  docProcBtn.disabled = false
                }

                // Add procedure card to old procedures carousel
                if (resp.procedure_card_html) {
                  this.addProcedureToCarousel(resp.procedure_card_html)
                }

                const radiologyRequest = this.isHakeemInsertionWorkflow() && resolvedIsRadiology
                const externalRadiologyRequest = radiologyRequest && resp.radiology_fulfillment_mode !== 'in_clinic'
                const uploadImages = () => {
                  if (resp.is_with_image) {
                    this.swalUploadImage(resp.upload_title, resp.upload_msg, resp.id, idsData[1], idsData[0], resp.upload_before, resp.upload_after, resp.upload, resp.approval_new_cancel)
                  }
                }
                const showRadiologyAuthorizationApproved = () => {
                  this.authorizationDialogUrl = null
                  this.showAuthorizationDialog('approved', resp.authorization_status_url, {
                    title: gon.locale === 'ar' ? 'تمت الموافقة على التفويض' : 'Authorization approved',
                    message: gon.locale === 'ar'
                      ? 'تمت الموافقة على طلب التفويض. ستستمر معالجة طلب الأشعة في الخلفية.'
                      : 'The authorization request was approved. The radiology request will continue processing in the background.'
                  })
                }

                if (this.isPendingWorkflowState(resp.authorization_state)) {
                  this.showAuthorizationDialog(resp.authorization_state, resp.authorization_status_url)
                  this.startAuthorizationPolling(
                    resp.authorization_status_url,
                    link,
                    externalRadiologyRequest
                      ? { onAuthorizationApproved: showRadiologyAuthorizationApproved }
                      : { onApproved: uploadImages }
                  )
                } else {
                  if (externalRadiologyRequest) {
                    showRadiologyAuthorizationApproved()
                  } else {
                    const successMessage = gon.locale === 'ar' ? 'تم إضافة الإجراء بنجاح' : 'Procedure added successfully'
                    Swal.fire({
                      position: 'top-end',
                      icon: 'success',
                      title: successMessage,
                      showConfirmButton: false,
                      timer: 1500
                    }).then(uploadImages)
                  }
                }

                // Show lock icon for blocked procedures
                if (resp.is_blocked) {
                  const lock = document.querySelector(`.lock-proc-${idsData[0]}_${idsData[1]}`)
                  if (lock) {
                    lock.style.display = ''
                    this.renderToothStatusMarker(lock, 'locked')
                  }
                }

                // If is_all_blocked=true, show lock icons on ALL procedures for the same tooth position
                if (resp.is_all_blocked && resp.teeth_position) {
                  const teethPos = resp.teeth_position

                  // Find all links with this tooth position across all procedures
                  const allLinksForThisTooth = document.querySelectorAll(`[class*="link_${teethPos}_"]`)

                  allLinksForThisTooth.forEach(link => {
                    // Skip the current procedure's tooth (already handled above)
                    const linkClasses = link.className.match(/link_(\d+)_(\w+)/)
                    if (!linkClasses) return

                    const linkTeethPos = linkClasses[1]
                    const linkProcCode = linkClasses[2]

                    if (linkTeethPos === teethPos.toString()) {
                      // Update is_blocked param
                      link.dataset.insertionIsBlockedParam = 'true'

                      // Find or create lock icon
                      let lockSpan = link.querySelector(`.lock-proc-${linkTeethPos}_${linkProcCode}`)
                      if (!lockSpan) {
                        // Create lock icon if it doesn't exist
                        lockSpan = document.createElement('span')
                        lockSpan.className = `mr-2 lock-proc-${linkTeethPos}_${linkProcCode}`
                        lockSpan.setAttribute('role', 'status')
                        lockSpan.style.cssText = 'position: absolute; top: 4%; left: 2%; margin: auto;'
                        this.renderToothStatusMarker(lockSpan, 'locked')
                        link.appendChild(lockSpan)
                      } else {
                        // Show existing lock icon
                        lockSpan.style.display = ''
                        this.renderToothStatusMarker(lockSpan, 'locked')
                      }

                      // Update image class if tooth has no insertion (grayscale -> grayscale with lock)
                      // If tooth already has an insertion (color/color-fake), keep it colored
                      const img = link.querySelector('img')
                      if (img && img.classList.contains('black-white')) {
                        // Keep grayscale, lock icon is now visible
                        // No need to change image class
                      }
                    }
                  })
                }

                if (approvalStatus) {
                  if (collapse) {
                    const nextSpan = collapse.querySelector('.color + span')
                    if (nextSpan) nextSpan.style.display = "block"
                  }

                  if (link) {
                    link.setAttribute('data-action', 'click->insertion#deleteConfirmedProcedure')
                    link.dataset.insertionInsurtionIdParam = resp.id
                  }
                }
              } else {
                // Revert
                const link = document.querySelector(`.link_${id}`)
                if (link) {
                  const colored = link.querySelector('.color')
                  if (colored) {
                    colored.classList.add("black-white")
                    colored.classList.remove("color")
                  }
                }

                const showBlockedOverride =
                  (String(resp.error) === "111") && (gon.user_type === 'admin')

                Swal.fire({
                  title: resp.title,
                  html: resp.msg,
                  icon: 'warning',
                  showCancelButton: showBlockedOverride,
                  confirmButtonColor: '#3085d6',
                  confirmButtonText: resp.confirm,
                  cancelButtonText: gon.locale === 'ar' ? 'إلغاء' : 'Cancel'
                }).then((result2) => {
                  if (!showBlockedOverride) return
                  if (!result2.isConfirmed) return

                  // Admin explicit override: resend with force_add=true
                  this.isSaving = true
                  $.ajax({
                    type: "post",
                    url: "/admin/procedures/insertion/add",
                    dataType: "json",
                    beforeSend: (xhr) => { xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content')) },
                    data: {
                      insured_number: gon.insured_number,
                      doctor_id: gon.doctor_id,
                      doctor_cost: result.value.doctorCost,
                      estimate_cost: estimateCost,
                      procedure_code: idsData[1],
                      status: true,
                      teeth_position: idsData[0],
                      procedure_id: procId,
                      approval_status: approvalStatus,
                      insurtion_date: result.value.insurtionDate,
                      locale: gon.locale,
                      contract_code: gon.contract_code,
                      force_add: true
                    },
                    success: (resp2) => {
                      this.isSaving = false
                      if (resp2 && resp2.success) {
                        // Re-run the existing success path by faking resp and returning early:
                        // simplest: just reload page section by navigating to auto-open url.
                        // (keeps behavior consistent without duplicating success logic here)
                        if (resp2.procedure_card_html) {
                          this.addProcedureToCarousel(resp2.procedure_card_html)
                        }
                        const msg = gon.locale === 'ar' ? 'تم إضافة الإجراء بنجاح' : 'Procedure added successfully'
                        Swal.fire({ position: 'top-end', icon: 'success', title: msg, showConfirmButton: false, timer: 1500 })
                      } else {
                        Swal.fire({ title: resp2?.title || 'Error', html: resp2?.msg || 'Failed', icon: 'error' })
                      }
                    },
                    error: () => {
                      this.isSaving = false
                      Swal.fire({ title: 'Error', text: 'Request failed', icon: 'error' })
                    }
                  })
                })
              }
            },
            error: () => {
              this.isSaving = false
              const spinner = document.querySelector(`.spinner-proc-${id}`)
              if (spinner) spinner.style.display = "none"
              Swal.fire({
                icon: 'error',
                title: gon.locale === 'ar' ? 'تعذر إرسال الطلب' : 'Unable to send request',
                text: gon.locale === 'ar' ? 'يرجى المحاولة مرة أخرى.' : 'Please try again.'
              })
            }
          })
        }
      })
    }

    if (preCheckResult) {
      openModalCallback(preCheckResult.showHistoryTab, preCheckResult.historyData)
    } else {
      // Check if procedure is blocked and has approved approval - if so, show history tab
      this.checkAndShowHistoryTab(procCode, teethPos, openModalCallback)
    }
  }

  checkAndShowHistoryTab(procedureCode, teethPosition, callback) {
    // Check if procedure is blocked and has approved approval
    // For doctors, use gon.doctor_id; for admins, try form fields
    let doctorId = gon.doctor_id
    if (!doctorId && gon.user_type === 'doctor') {
      // For doctors, doctor_id should be set in gon, but fallback to current_doctor if available
      doctorId = gon.doctor_id || $('#procedures_insurtions_doctor_email').val() || $('#doctor_email').val()
    } else if (!doctorId && gon.user_type === 'admin') {
      // For admins, try form fields
      doctorId = $('#procedures_insurtions_doctor_email').val() || $('#doctor_email').val()
    }

    console.log('checkAndShowHistoryTab called with:', {
      procedureCode,
      teethPosition,
      insured_number: gon.insured_number,
      doctor_id: doctorId,
      gon_doctor_id: gon.doctor_id,
      gon_user_type: gon.user_type,
      form_doctor_email: $('#procedures_insurtions_doctor_email').val(),
      form_doctor_email_2: $('#doctor_email').val()
    })

    if (!procedureCode || !teethPosition || !gon.insured_number) {
      console.error('Missing required parameters for history check', {
        procedureCode,
        teethPosition,
        insured_number: gon.insured_number
      })
      if (callback) {
        callback(false, [])
      }
      return
    }

    $.ajax({
      type: "get",
      url: "/admin/procedures/get/history",
      dataType: "json",
      beforeSend: (xhr) => {
        xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content'))
        console.log('Sending AJAX request to /admin/procedures/get/history')
      },
      data: {
        insured_number: gon.insured_number,
        procedure_code: procedureCode,
        teeth_position: teethPosition,
        doctor_id: doctorId,
        contract_code: gon.contract_code,
        locale: gon.locale
      },
      success: (result) => {
        console.log('AJAX success - full result:', result)
        // Show history tab only if procedure is blocked AND has approved approval
        const showHistoryTab = result.success && result.show_history_tab === true
        console.log('History check result:', {
          success: result.success,
          show_history_tab: result.show_history_tab,
          is_blocked: result.is_blocked,
          has_approved_approval: result.has_approved_approval,
          history_count: result.history ? result.history.length : 0,
          willShowTab: showHistoryTab
        })
        if (callback) {
          console.log('Calling callback with showHistoryTab:', showHistoryTab)
          callback(showHistoryTab, result.history || [])
        } else {
          console.error('No callback provided to checkAndShowHistoryTab')
        }
      },
      error: (xhr, status, error) => {
        console.error('AJAX error:', {
          status: status,
          error: error,
          responseText: xhr.responseText,
          statusCode: xhr.status
        })
        // On error, don't show history tab
        if (callback) {
          callback(false, [])
        }
      }
    })
  }

  updateInsuredProcedure(procId, result, toothNo = null) {
    let estimateCost = 0
    let approvalStatus = false

    if (result.value.estimateCost !== "") {
      estimateCost = result.value.estimateCost
      approvalStatus = true
    }

    $.ajax({
      type: "put",
      url: "/admin/procedures/insertion/update",
      dataType: "json",
      beforeSend: (xhr) => { xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content')) },
      data: {
        doctor_cost: result.value.doctorCost,
        estimate_cost: estimateCost,
        procedure_id: procId,
        approval_status: approvalStatus,
        insurtion_date: result.value.insurtionDate,
        locale: gon.locale
      },
      success: (resp) => {
        if (resp.success) {
          // Keep tooth link dataset in sync so reopening shows updated values
          if (toothNo) {
            const link = document.querySelector(`.link_${toothNo}`)
            if (link) {
              if (resp.insurtion_date !== undefined) link.dataset.insertionInsertionDateParam = resp.insurtion_date
              if (resp.doctor_cost !== undefined) link.dataset.insertionDoctorCostParam = resp.doctor_cost
              if (resp.estimate_cost !== undefined) link.dataset.insertionEstimateCostParam = resp.estimate_cost
              if (resp.is_with_image !== undefined) link.dataset.insertionIsAttachedParam = resp.is_with_image
              if (resp.is_blocked !== undefined) link.dataset.insertionIsBlockedParam = resp.is_blocked
            }
          }

          // Update procedure card in carousel
          if (resp.procedure_card_html) {
            this.updateProcedureInCarousel(procId, resp.procedure_card_html)
          }

          // Show success message
          const successMessage = gon.locale === 'ar' ? 'تم تحديث الإجراء بنجاح' : 'Procedure updated successfully'
          Swal.fire({
            position: 'top-end',
            icon: 'success',
            title: successMessage,
            showConfirmButton: false,
            timer: 1500
          })
        }
      },
      error: () => {
        const errorMessage = gon.locale === 'ar' ? 'حدث خطأ أثناء التحديث' : 'Error updating procedure'
        Swal.fire({
          icon: 'error',
          title: errorMessage,
          showConfirmButton: true
        })
      }
    })
  }

  getProcedureInsurtionImages(no, procId, labels, insertionDate, doctorCost, estimateCost, coverageRate, defaultPrice, isBlocked, isAttached = false, showHistoryTab = false, historyData = undefined) {
    // Extract teeth position and procedure code from 'no' parameter (format: teethPos_procCode)
    const noData = no.split("_")
    const teethPos = noData[0]
    const procCode = noData[1]

    $.ajax({
      type: "get",
      url: "/admin/procedures/get/uploaded_images",
      dataType: "json",
      beforeSend: (xhr) => { xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content')) },
      data: {
        procedure_id: procId,
        doctor_id: gon.doctor_id,
        locale: gon.locale
      },
      success: (result) => {
        if (result.success) {
          if (result.id == -1) {
            this.setInsuredProcedure(no, procId, labels, [], "", "", "", coverageRate, defaultPrice, insertionDate, isAttached, null, { showHistoryTab: showHistoryTab, historyData: historyData })
          } else {
            this.isSaving = false
            const deleteMsg = (gon && gon.delete_msg) ? gon.delete_msg : ''
            const firstImg = result.first_image && result.first_image !== '' ? result.first_image : '/assets/xray.jpg'
            const secondImg = result.second_image && result.second_image !== '' ? result.second_image : '/assets/xray.jpg'

            // Check if current doctor is the creator of this procedure
            const isCreator = result.doctor_id && gon.doctor_id && result.doctor_id === gon.doctor_id

            // Determine actual blocked status from API response (more reliable than passed parameter)
            const actualIsBlocked = result.is_blocked || false

            const imagesHtml = `<div class="row mb-3">
              <div class="col-6 d-flex"><div class="card flex-fill"><img alt="Card Image" src="${firstImg}" class="card-img-top" style="max-height: 200px; object-fit: contain;" onerror="this.src='/assets/xray.jpg'"><div class="card-header"><h5 class="card-title mb-0">${result.first_image_title}</h5></div></div></div>
              <div class="col-6 d-flex"><div class="card flex-fill"><img alt="Card Image" src="${secondImg}" class="card-img-top" style="max-height: 200px; object-fit: contain;" onerror="this.src='/assets/xray.jpg'"><div class="card-header"><h5 class="card-title mb-0">${result.second_image_title}</h5></div></div></div>
              </div>
              <button type="button" data-action="click->insertion#triggerUpload" data-upload-title="${result.upload_title}" data-upload-msg="${result.upload_msg}" data-proc-id="${result.id}" data-proc-code="${procCode}" data-teeth-pos="${teethPos}" data-upload-before="${result.upload_before}" data-upload-after="${result.upload_after}" data-upload-btn="${result.upload}" data-cancel-btn="${result.approval_new_cancel}" class="btn btn-info mt-2 mb-3">${result.upload}</button>
              <div></div>`

            // Extract procedure code and teeth position for history check
            const noData = no.split("_")
            const teethPosForHistory = noData[0]
            const procCodeForHistory = noData[1]

            const handleShowModal = (showHistoryTabVal, historyDataVal) => {
              // For doctors viewing existing procedures (not new)
              const isDoctor = gon.user_type === 'doctor'
              const isAdmin = gon.user_type === 'admin'
              const isExistingProcedure = procId && procId !== -1 && procId !== "-1"

              // Admins may update the date even when the procedure is locally
              // blocked. Admin cost fields remain read-only in showProcedureModal.
              const adminCanEditDate = isAdmin && isExistingProcedure
              const shouldDisableAllFields = !adminCanEditDate && (actualIsBlocked || (isDoctor && isExistingProcedure))
              const shouldShowSaveButton = adminCanEditDate || (!actualIsBlocked && !(isDoctor && isExistingProcedure))
              const shouldAllowImagesOnly = !adminCanEditDate && ((actualIsBlocked && isCreator) || (isDoctor && isExistingProcedure && isCreator))

              this.showProcedureModal({
                dateLabel: labels.date,
                doctorCostLabel: labels.doctorCost,
                estimateCostLabel: labels.estimateCost,
                insurtionDate: result.insurtion_date || insertionDate,
                doctorCost: result.doctor_cost || doctorCost,
                estimateCost: result.estimate_cost || estimateCost,
                imagesHtml: imagesHtml,
                coverageRate: coverageRate,
                defaultPrice: defaultPrice,
                isBlocked: actualIsBlocked,
                disableAllFields: shouldDisableAllFields,
                allowImagesOnly: shouldAllowImagesOnly,
                showSaveButton: shouldShowSaveButton,
                showHistoryTab: showHistoryTabVal,
                procedureCode: procCodeForHistory,
                teethPosition: teethPosForHistory,
                historyData: historyDataVal,
                showAddNewButton: true,
                insertionId: procId,
                isAttached: isAttached,
                onSave: (resultdata) => {
                  this.isSaving = false
                  this.updateInsuredProcedure(procId, resultdata, no)
                }
              })
            }

            // Check if historyData is already provided (from deleteConfirmedProcedure or selectTeeth)
            if (historyData !== undefined) {
              handleShowModal(showHistoryTab, historyData)
            } else {
              // Otherwise fetch it
              this.checkAndShowHistoryTab(procCodeForHistory, teethPosForHistory, handleShowModal)
            }
          }
        } else {
          this.isSaving = false
        }
      },
      error: () => {
        this.isSaving = false
        if (procId == -1) {
          this.setInsuredProcedure(no, procId, labels, [], "", "", "", coverageRate, defaultPrice, insertionDate, isAttached, null, { showHistoryTab: showHistoryTab, historyData: historyData })
        }
      }
    })
  }

  triggerUpload(event) {
    event.preventDefault()
    event.stopPropagation()
    const d = event.currentTarget.dataset
    this.swalUploadImage(d.uploadTitle, d.uploadMsg, d.procId, d.procCode, d.teethPos, d.uploadBefore, d.uploadAfter, d.uploadBtn, d.cancelBtn)
  }

  swalUploadImage(uploadTitle, uploadMsg, id, procCode, teethPos, uploadBefore, uploadAfter, upload, approvalNewCancel) {
    Swal.fire({
      title: uploadTitle,
      html: `<div style="overflow-x: hidden;">${uploadMsg}<input type='hidden' value='${id}' id='swal_proc_id'><div class='form-group row'><label class='col-form-label col-12 col-md-4 mt-2'>${uploadBefore}</label><div class='col-12 col-md-8'><input class='swal2-file form-control mb-2' id='first_file' accept='image/*' type='file'></div></div><div class='form-group row'><label class='col-form-label col-12 col-md-4 mt-2'>${uploadAfter}</label><div class='col-12 col-md-8'><input id='second_file' accept='image/*' class='swal2-file form-control' type='file'></div></div></div>`,
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: upload,
      cancelButtonText: approvalNewCancel
    }).then((file) => {
      if (file.value) {
        var formData = new FormData();
        var first_file = $('.swal2-file')[0].files[0];
        var second_file = $('.swal2-file')[1].files[0];
        formData.append("first_file", first_file);
        formData.append("second_file", second_file);
        formData.append("proc_id", document.getElementById('swal_proc_id').value);

        $.ajax({
          headers: { 'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content') },
          method: 'post',
          url: '/admin/procedures/insertion/upload_procedure_images',
          data: formData,
          processData: false,
          contentType: false,
          success: (resp) => {
            const uploadSuccessMsg = gon.locale === 'ar' ? 'تم رفع الصور بنجاح' : 'Images uploaded successfully'

            // Fetch updated images from server to get new blob URLs
            $.ajax({
              type: "get",
              url: "/admin/procedures/get/uploaded_images",
              dataType: "json",
              beforeSend: (xhr) => { xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content')) },
              data: {
                procedure_id: id,
                doctor_id: gon.doctor_id,
                locale: gon.locale
              },
              success: (result) => {
                if (result.success && result.id != -1) {
                  // Update the images in the modal with new URLs
                  const firstImg = result.first_image && result.first_image !== '' ? result.first_image : '/assets/xray.jpg'
                  const secondImg = result.second_image && result.second_image !== '' ? result.second_image : '/assets/xray.jpg'

                  const imagesHtml = `<div class="row mb-3">
                    <div class="col-6 d-flex"><div class="card flex-fill"><img alt="Card Image" src="${firstImg}" class="card-img-top" style="max-height: 200px; object-fit: contain;" onerror="this.src='/assets/xray.jpg'"><div class="card-header"><h5 class="card-title mb-0">${result.first_image_title}</h5></div></div></div>
                    <div class="col-6 d-flex"><div class="card flex-fill"><img alt="Card Image" src="${secondImg}" class="card-img-top" style="max-height: 200px; object-fit: contain;" onerror="this.src='/assets/xray.jpg'"><div class="card-header"><h5 class="card-title mb-0">${result.second_image_title}</h5></div></div></div>
                    </div>
                    <button type="button" data-action="click->insertion#triggerUpload" data-upload-title="${result.upload_title}" data-upload-msg="${result.upload_msg}" data-proc-id="${result.id}" data-proc-code="${procCode}" data-teeth-pos="${teethPos}" data-upload-before="${result.upload_before}" data-upload-after="${result.upload_after}" data-upload-btn="${result.upload}" data-cancel-btn="${result.approval_new_cancel}" class="btn btn-info mt-2 mb-3">${result.upload}</button>
                    <div></div>`

                  $('#procedure_modal_images').html(imagesHtml)
                }
              }
            })

            Swal.fire({
              position: 'top-end',
              icon: 'success',
              title: uploadSuccessMsg,
              showConfirmButton: false,
              timer: 1800
            })
          },
          error: () => {
            Swal.fire({ type: 'error', title: 'Oops...', text: 'Something went wrong!' })
          }
        })
      }
    })
  }

  showProcedureModal(options) {
    const { dateLabel, doctorCostLabel, estimateCostLabel, insurtionDate, doctorCost, estimateCost, imagesHtml, coverageRate, defaultPrice, isBlocked, disableAllFields, allowImagesOnly, showSaveButton, onSave, showHistoryTab, procedureCode, teethPosition, historyData, showAddNewButton, insertionId, isAttached, approvalStatus, isNewInsertion, isRadiology, needsRadiology } = options
    const resolvedIsRadiology = isRadiology === true || this.isRadiologyProcedure(procedureCode)
    const procedureItem = Array.from(document.querySelectorAll('.procedure-item')).find((item) => item.dataset.procedureCode === String(procedureCode))
    const resolvedNeedsRadiology = needsRadiology === true || procedureItem?.dataset.needsRadiology === 'true'
    const linkedRadiologyStatus = this.isHakeemInsertionWorkflow() && Array.from(document.querySelectorAll('.linked-radiology-status-data')).find((element) =>
      element.dataset.procedureCode === String(procedureCode) && element.dataset.toothPosition === String(teethPosition)
    )
    this.openProcedureCode = String(procedureCode)
    this.openTeethPosition = String(teethPosition)

    // Default: when editing an existing insertion, always allow "Add New Insertion"
    // (auto-open path from latest-procedures cards may not pass showAddNewButton)
    const resolvedShowAddNewButton =
      (showAddNewButton === undefined || showAddNewButton === null) ? !isNewInsertion : showAddNewButton

    // Show/hide tabs based on showHistoryTab flag
    console.log('showProcedureModal called with:', {
      showHistoryTab: showHistoryTab,
      procedureCode: procedureCode,
      teethPosition: teethPosition,
      historyData: historyData,
      historyDataLength: historyData ? historyData.length : 0,
      allOptions: options
    })

    if (showHistoryTab && procedureCode && teethPosition) {
      console.log('Showing history tab - conditions met, setting display to flex')
      $('#procedure_modal_tabs').show().css('display', 'flex')
      // Load history immediately if data is available, otherwise show empty state
      if (historyData && historyData.length > 0) {
        this.loadProcedureHistory(procedureCode, teethPosition, historyData)
      } else {
        // Show empty state if no history yet
        $('#procedure_history_data').hide()
        $('#procedure_history_loading').hide()
        $('#procedure_history_empty').show()
      }
      // Set up history tab click handler to fetch if not already loaded
      $('#procedure_history_tab').off('shown.bs.tab').on('shown.bs.tab', () => {
        const historyContainer = $('#procedure_history_data')
        const historyEmpty = $('#procedure_history_empty')
        if (!historyContainer.is(':visible') && !historyEmpty.is(':visible')) {
          this.fetchAndLoadHistory(procedureCode, teethPosition)
        }
      })
    } else {
      console.log('Hiding history tab - showHistoryTab:', showHistoryTab, 'procedureCode:', procedureCode, 'teethPosition:', teethPosition)
      $('#procedure_modal_tabs').hide()
      // Make sure details tab is active
      $('#procedure_details_tab').tab('show')
    }

    $('#procedure_modal_date_lbl').text(dateLabel)
    $('#procedure_modal_doctor_cost_lbl').text(doctorCostLabel)
    $('#procedure_modal_estimate_cost_lbl').text(estimateCostLabel)

    $('#procedure_modal_insurtion_date').val(insurtionDate || '')
    const needsRadiologyDob = this.isHakeemInsertionWorkflow() && isNewInsertion && resolvedIsRadiology && !gon.insured_date_of_birth
    $('#procedure_modal_date_of_birth').val(gon.insured_date_of_birth || '').attr('max', gon.today_date || '')
    $('#procedure_modal_date_of_birth').prop('required', needsRadiologyDob)
    $('#procedure_modal_date_of_birth_group').toggle(needsRadiologyDob)

    // Check if defaultPrice is valid
    const validDefaultPrice = !isNaN(parseFloat(defaultPrice))

    // Check if doctorCost/estimateCost is defined and not null (allow 0)
    // If empty, fallback to defaultPrice if valid
    const finalDocCost = (doctorCost !== undefined && doctorCost !== null && doctorCost !== '') ? doctorCost : (validDefaultPrice ? defaultPrice : '')
    const finalEstCost = (estimateCost !== undefined && estimateCost !== null && estimateCost !== '') ? estimateCost : (validDefaultPrice ? defaultPrice : '')

    $('#procedure_modal_doctor_cost').val(finalDocCost)
    $('#procedure_modal_estimate_cost').val(finalEstCost)

    // Show/hide images section based on whether imagesHtml is provided
    if (imagesHtml && imagesHtml.trim() !== '') {
      $('#procedure_modal_images').html(imagesHtml).show()
    } else {
      $('#procedure_modal_images').html('').hide()
    }

    // Calculate and fill the patient and insurance shares.
    console.log('showProcedureModal called with:', {
      doctorCost: doctorCost,
      estimateCost: estimateCost,
      coverageRate: coverageRate,
      defaultPrice: defaultPrice,
      isBlocked: isBlocked,
      disableAllFields: disableAllFields,
      allowImagesOnly: allowImagesOnly,
      showSaveButton: showSaveButton,
      onSave: onSave,
      showHistoryTab: showHistoryTab,
      procedureCode: procedureCode,
      teethPosition: teethPosition,
      historyData: historyData,
      showAddNewButton: showAddNewButton,
      isAttached: isAttached
    })
    const docCostVal = parseFloat(finalDocCost) || 0
    const eCostVal = parseFloat(finalEstCost) || 0
    const covRateVal = parseFloat(coverageRate) || 0

    // coverageRate is the patient's co-pay percentage. Shares are calculated
    // from the final estimated/gross claim amount, not the doctor's request.
    const grossAmount = eCostVal
    const patientShare = grossAmount * (covRateVal / 100.0)
    const netValue = grossAmount - patientShare

    $('#procedure_modal_patient_share').val(patientShare.toFixed(2))
    $('#procedure_modal_net_value').val(netValue.toFixed(2))

    // Check if procedure is blocked
    const procedureIsBlocked = (isBlocked === true || isBlocked === 'true')

    // Disable/Enable inputs based on context
    if (disableAllFields) {
      // Completely disable all fields (for read-only mode or locked procedures)
      $('#procedure_modal_estimate_cost').attr('disabled', 'disabled').attr('readonly', 'readonly')
      $('#procedure_modal_doctor_cost').attr('disabled', 'disabled').attr('readonly', 'readonly')
      $('#procedure_modal_insurtion_date').attr('disabled', 'disabled').attr('readonly', 'readonly')
    } else if (gon.user_type === 'doctor') {
      // For doctors adding/editing: allow date only, disable doctor cost and hide estimate cost
      $('#procedure_modal_estimate_cost').attr('disabled', 'disabled').attr('readonly', 'readonly')
      $('#procedure_modal_doctor_cost').attr('disabled', 'disabled').attr('readonly', 'readonly')
      // For new insertions, disable date field (it's set to today automatically)
      if (isNewInsertion) {
        $('#procedure_modal_insurtion_date').attr('disabled', 'disabled').attr('readonly', 'readonly')
      } else {
        $('#procedure_modal_insurtion_date').removeAttr('disabled').removeAttr('readonly')
      }
    } else {
      // For admins: doctor cost always disabled, estimate cost hidden (values set by model)
      $('#procedure_modal_estimate_cost').attr('disabled', 'disabled').attr('readonly', 'readonly')
      $('#procedure_modal_doctor_cost').attr('disabled', 'disabled').attr('readonly', 'readonly')

      // For admins: ALWAYS allow date editing, but constrained to contract period
      const $dateInput = $('#procedure_modal_insurtion_date')
      $dateInput.removeAttr('disabled').removeAttr('readonly')

      if (gon.contract_start_date) {
        $dateInput.attr('min', gon.contract_start_date)
      }
      if (gon.contract_end_date) {
        $dateInput.attr('max', gon.contract_end_date)
      }
    }

    // Always hide estimate cost field (values are set automatically by model from procedure_default_price)
    $('#procedure_modal_estimate_cost').closest('.form-group').hide()

    // Handle save button and image upload button visibility
    if (allowImagesOnly) {
      // Locked procedure: hide save button, enable image upload only if creator matches
      $('#procedure_modal_save_btn').attr('disabled', 'disabled').addClass('btn-secondary').removeClass('btn-primary').hide()
      $('#procedure_modal_images button').removeAttr('disabled').show()
    } else if (showSaveButton === false) {
      // Read-only mode: hide save button and disable image upload
      $('#procedure_modal_save_btn').attr('disabled', 'disabled').addClass('btn-secondary').removeClass('btn-primary').hide()
      $('#procedure_modal_images button').attr('disabled', 'disabled').hide()
    } else {
      // Normal mode: show save button and enable image upload
      $('#procedure_modal_save_btn').removeAttr('disabled').removeClass('btn-secondary').addClass('btn-primary').show()
      $('#procedure_modal_images button').removeAttr('disabled')
    }
    $('#procedure_modal_save_btn').text(
      this.isHakeemInsertionWorkflow() && isNewInsertion && resolvedIsRadiology
        ? (gon.locale === 'ar' ? 'إرسال طلب الأشعة' : 'Send Radiology Request')
        : (gon.save_label || 'Save')
    )

    // A radiology request belongs to the insertion being created. If this
    // modal cannot save that insertion (read-only, locked, or images-only),
    // do not let the doctor start a detached radiology approval request.
    const canCreateInsertion = !allowImagesOnly && showSaveButton !== false
    const showRadiologyRequest = this.isHakeemInsertionWorkflow() && resolvedNeedsRadiology
    $('#procedure_modal_radiology_request').toggle(showRadiologyRequest)
    if (showRadiologyRequest && linkedRadiologyStatus) {
      const radiologyName = this.escapeHtml(linkedRadiologyStatus.dataset.radiologyProcedureName)
      const radiologyCode = this.escapeHtml(linkedRadiologyStatus.dataset.radiologyProcedureCode)
      const statusLabel = this.escapeHtml(linkedRadiologyStatus.dataset.statusLabel)
      $('#procedure_modal_radiology_message').html(
        `<strong>${statusLabel}</strong><br>${radiologyName} (${radiologyCode})`
      )
      $('#procedure_modal_radiology_request_btn').hide()
      const blocksTreatment = linkedRadiologyStatus.dataset.blocksTreatment === 'true'
      if (blocksTreatment) {
        $('#procedure_modal_save_btn')
          .prop('disabled', true)
          .addClass('btn-secondary')
          .removeClass('btn-primary')
          .text(gon.locale === 'ar' ? 'بانتظار نتائج الأشعة' : 'Waiting for Radiology Results')
      }
      const resultViewUrl = linkedRadiologyStatus.dataset.resultViewUrl
      $('#procedure_modal_radiology_results_btn')
        .toggle(linkedRadiologyStatus.dataset.status === 'results_available' && !!resultViewUrl)
        .off('click')
        .on('click', () => this.showSubmittedRadiologyResult(resultViewUrl, gon.locale === 'ar'))
      this.renderLinkedRadiologyTicket(
        linkedRadiologyStatus.dataset.ticketNumber,
        linkedRadiologyStatus.dataset.ticketPrintUrl,
        linkedRadiologyStatus.dataset.status,
        linkedRadiologyStatus.dataset.ticketStartUrl
      )
      this.refreshLinkedRadiologyStatus(linkedRadiologyStatus)
    } else {
      $('#procedure_modal_radiology_message').text(
        gon.locale === 'ar'
          ? 'سيتم أولاً إرسال طلب تفويض لهذا العلاج، وبعد الموافقة عليه سيُرسل طلب تفويض إجراء الأشعة الذي تختاره.'
          : 'Authorization for this treatment will be requested first. After it is approved, authorization for your selected radiology procedure will be requested.'
      )
      $('#procedure_modal_radiology_request_btn')
        .prop('disabled', !canCreateInsertion)
        .show()
      $('#procedure_modal_radiology_results_btn').hide().off('click')
      this.renderLinkedRadiologyTicket('', '', '', '')
    }
    $('#procedure_modal_radiology_request_btn').off('click').on('click', () => {
      this.openRadiologyProcedureRequest(procedureCode, teethPosition)
    })

    $('#procedure_modal_save_btn').off('click').on('click', () => {
      const iDate = $('#procedure_modal_insurtion_date').val()
      const dCost = $('#procedure_modal_doctor_cost').val()
      const eCost = $('#procedure_modal_estimate_cost').val()
      const dateOfBirth = $('#procedure_modal_date_of_birth').val()

      if (!iDate || !dCost) return;
      if (needsRadiologyDob && !dateOfBirth) {
        $('#procedure_modal_date_of_birth')[0].reportValidity()
        return
      }

      if (typeof onSave === 'function') {
        onSave({ value: { insurtionDate: iDate, doctorCost: dCost, estimateCost: eCost, dateOfBirth: dateOfBirth } })
      }
      this.currentTeethNo = null
      $('#procedureEditModal').modal('hide')
    })

    $('#procedureEditModal').off('hidden.bs.modal').on('hidden.bs.modal', () => {
      if (this.currentTeethNo !== null) {
        const link = document.querySelector(`.link_${this.currentTeethNo}`)
        if (link) {
          const colored = link.querySelector('.color')
          if (colored) {
            colored.classList.add("black-white")
            colored.classList.remove("color")
          }
        }
        const spinner = document.querySelector(`.spinner-proc-${this.currentTeethNo}`)
        if (spinner) spinner.style.display = "none"
        this.isSaving = false
        this.currentTeethNo = null
      }
    })

    // Show/Hide Add New Insertion button and handle approval workflow for doctors
    const addNewBtn = $('#procedure_modal_add_new_btn')
    if (resolvedShowAddNewButton) {
      if (gon.user_type === 'admin') {
        if (!isNewInsertion && resolvedNeedsRadiology && linkedRadiologyStatus && insertionId) {
          this.configureTreatmentClaimButton(
            addNewBtn,
            insertionId,
            linkedRadiologyStatus,
            procedureCode,
            teethPosition,
            isBlocked
          )
        } else {
          // Admins bypass local blocking rules, while insurance authorization and
          // claim submission continue through the selected doctor's workflow.
          addNewBtn.show()
            .removeClass('btn-success btn-secondary')
            .addClass('btn-info')
            .prop('disabled', false)
            .text(gon.locale === 'ar' ? 'إضافة إجراء جديد' : 'Add New Insertion')

          addNewBtn.off('click').on('click', () => {
            this.addNewInsertion(procedureCode, teethPosition, {
              date: dateLabel,
              doctorCost: doctorCostLabel,
              estimateCost: estimateCostLabel
            }, coverageRate, defaultPrice, isBlocked, isAttached, resolvedIsRadiology)
          })
        }
      } else if (gon.user_type === 'doctor') {
        // Doctor: show button
        addNewBtn.show()

        if (!isNewInsertion && resolvedNeedsRadiology && linkedRadiologyStatus && insertionId) {
          this.configureTreatmentClaimButton(
            addNewBtn,
            insertionId,
            linkedRadiologyStatus,
            procedureCode,
            teethPosition,
            isBlocked
          )
        } else if (isBlocked) {
          // Blocked procedure: requires approval workflow
          // Set initial state while fetching approval status
          addNewBtn.removeClass('btn-success btn-secondary')
            .addClass('btn-info')
            .prop('disabled', false)
            .text(gon.locale === 'ar' ? 'طلب موافقة لإضافة إجراء جديد' : 'Request Approval for New Insertion')

          // Fetch approval status to show correct button state
          this.fetchApprovalStatus(procedureCode, teethPosition, (approval) => {
            if (!approval || (approval.approval_status === true && approval.is_proceeded === true)) {
              // No approval request exists OR approval was used (approved and proceeded)
              // Show "Request Approval" button
              addNewBtn.removeClass('btn-success btn-secondary')
                .addClass('btn-info')
                .prop('disabled', false)
                .text(gon.locale === 'ar' ? 'طلب موافقة لإضافة إجراء جديد' : 'Request Approval for New Insertion')

              addNewBtn.off('click').on('click', () => {
                this.requestApprovalForNewInsertion(procedureCode, teethPosition, addNewBtn)
              })
            } else if (approval.approval_status === false && approval.is_proceeded === false) {
              // Pending approval
              addNewBtn.removeClass('btn-info btn-success')
                .addClass('btn-secondary')
                .prop('disabled', true)
                .text(gon.locale === 'ar' ? 'في انتظار الموافقة' : 'Waiting for Approval')

              addNewBtn.off('click')
            } else if (approval.approval_status === false && approval.is_proceeded === true) {
              // Rejected
              addNewBtn.removeClass('btn-info btn-success')
                .addClass('btn-secondary')
                .prop('disabled', true)
                .text(gon.locale === 'ar' ? 'مرفوض' : 'Rejected')

              addNewBtn.off('click')
            } else if (approval.approval_status === true && approval.is_proceeded === false) {
              // Approved and not yet used
              addNewBtn.removeClass('btn-info btn-secondary')
                .addClass('btn-success')
                .prop('disabled', false)
                .text(gon.locale === 'ar' ? 'موافق عليه، إضافة جديد' : 'Approved, Add New')

              addNewBtn.off('click').on('click', () => {
                this.addNewInsertion(procedureCode, teethPosition, {
                  date: dateLabel,
                  doctorCost: doctorCostLabel,
                  estimateCost: estimateCostLabel
                }, coverageRate, defaultPrice, isBlocked, isAttached, resolvedIsRadiology)
              })
            }
          })
        } else {
          // Non-blocked procedure: no approval needed, add directly
          addNewBtn.removeClass('btn-secondary btn-info')
            .addClass('btn-success')
            .prop('disabled', false)
            .text(gon.locale === 'ar' ? 'إضافة إجراء جديد' : 'Add New Insertion')

          addNewBtn.off('click').on('click', () => {
            this.addNewInsertion(procedureCode, teethPosition, {
              date: dateLabel,
              doctorCost: doctorCostLabel,
              estimateCost: estimateCostLabel
            }, coverageRate, defaultPrice, isBlocked, isAttached, resolvedIsRadiology)
          })
        }
      } else {
        addNewBtn.hide()
      }
    } else {
      addNewBtn.hide()
    }

    $('#procedureEditModal').modal('show')
  }

  configureTreatmentClaimButton(button, insertionId, linkedRadiologyStatus, procedureCode, teethPosition, isBlocked) {
    const isArabic = gon.locale === 'ar'
    const radiologyReady = linkedRadiologyStatus.dataset.status === 'results_available'
    const procedureBlocksNextInsertion = isBlocked === true || isBlocked === 'true'
    const setState = (state) => {
      button.removeClass('btn-info btn-success btn-secondary')
      if (state === 'approved' && procedureBlocksNextInsertion) {
        button.addClass('btn-info').prop('disabled', false)
          .text(isArabic ? 'طلب موافقة لإضافة إجراء جديد' : 'Request Approval for New Insertion')
        button.off('click').on('click', () => {
          this.requestApprovalForNewInsertion(procedureCode, teethPosition, button)
        })
      } else if (state === 'approved') {
        button.addClass('btn-secondary').prop('disabled', true)
          .text(isArabic ? 'تمت الموافقة على المطالبة' : 'Claim Approved')
        button.off('click')
      } else if (state === 'waiting_for_claim') {
        button.addClass('btn-secondary').prop('disabled', true)
          .text(isArabic ? 'بانتظار موافقة المطالبة' : 'Waiting for Claim Approval')
        button.off('click')
      } else if (!radiologyReady) {
        button.addClass('btn-secondary').prop('disabled', true)
          .text(isArabic ? 'بانتظار نتائج الأشعة' : 'Waiting for Radiology Results')
        button.off('click')
      } else {
        button.addClass('btn-success').prop('disabled', false)
          .text(isArabic ? 'إرسال المطالبة' : 'Submit Claim')
        button.off('click').on('click', () => this.submitAuthorizedTreatmentClaim(insertionId, button))
      }
    }

    setState(null)
    $.ajax({
      type: 'get',
      url: `/admin/procedures/insertion/${insertionId}/authorization_status`,
      dataType: 'json',
      data: { locale: gon.locale },
      success: (response) => {
        if (response.success) setState(response.state)
      }
    })
  }

  submitAuthorizedTreatmentClaim(insertionId, button) {
    const isArabic = gon.locale === 'ar'
    Swal.fire({
      icon: 'question',
      title: isArabic ? 'إرسال المطالبة؟' : 'Submit claim?',
      text: isArabic
        ? 'سيتم استخدام تفويض العلاج الموافق عليه لإرسال المطالبة.'
        : 'The approved treatment authorization will be used to submit this claim.',
      showCancelButton: true,
      confirmButtonText: isArabic ? 'إرسال المطالبة' : 'Submit Claim',
      cancelButtonText: gon.cancel || (isArabic ? 'إلغاء' : 'Cancel')
    }).then((result) => {
      if (!result.isConfirmed) return
      button.prop('disabled', true).addClass('btn-secondary').removeClass('btn-success')
        .text(isArabic ? 'جارٍ إرسال المطالبة' : 'Submitting Claim')
      $.ajax({
        type: 'post',
        url: `/doctor/procedures/insertion/${insertionId}/submit_claim`,
        dataType: 'json',
        beforeSend: (xhr) => xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content')),
        data: { locale: gon.locale },
        success: (response) => {
          if (!response.success) return
          button.prop('disabled', true).text(
            response.state === 'approved'
              ? (isArabic ? 'تمت الموافقة على المطالبة' : 'Claim Approved')
              : (isArabic ? 'بانتظار موافقة المطالبة' : 'Waiting for Claim Approval')
          )
          if (response.status_url && response.state !== 'approved') {
            this.startAuthorizationPolling(response.status_url, null)
          }
        },
        error: (xhr) => {
          button.prop('disabled', false).removeClass('btn-secondary').addClass('btn-success')
            .text(isArabic ? 'إرسال المطالبة' : 'Submit Claim')
          Swal.fire({
            icon: 'error',
            title: isArabic ? 'تعذر إرسال المطالبة' : 'Unable to Submit Claim',
            text: xhr.responseJSON?.error || (isArabic ? 'يرجى المحاولة مرة أخرى.' : 'Please try again.')
          })
        }
      })
    })
  }

  addNewInsertion(procedureCode, teethPosition, labels, coverageRate, defaultPrice, isBlocked, isAttached, isRadiology = false) {
    console.log('addNewInsertion called with:', { procedureCode, teethPosition, coverageRate, defaultPrice, isBlocked, isAttached })
    console.log('Labels:', labels)

    // Ensure we have valid coverage rate and default price
    const validCoverageRate = parseFloat(coverageRate) || 0
    const validDefaultPrice = parseFloat(defaultPrice) || 0
    console.log('Parsed values:', { validCoverageRate, validDefaultPrice })
    const repeatTitle = this.isHakeemInsertionWorkflow() && isRadiology
      ? (gon.locale === 'ar' ? 'هل أنت متأكد من إرسال طلب أشعة جديد؟' : 'Are you sure you want to send a new radiology request?')
      : (gon.locale === 'ar' ? 'هل أنت متأكد من إضافة إجراء جديد؟' : 'Are you sure you want to add a new insertion?')
    const repeatMessage = this.isHakeemInsertionWorkflow() && isRadiology
      ? (gon.locale === 'ar' ? 'سيتم إنشاء طلب أشعة جديد لنفس السن.' : 'A new radiology request will be created for the same tooth.')
      : (gon.locale === 'ar' ? 'سيتم إضافة إجراء جديد لنفس السن' : 'A new procedure will be added for the same tooth')
    Swal.fire({
      title: repeatTitle,
      text: repeatMessage,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: gon.confirm,
      cancelButtonText: gon.cancel
    }).then((result) => {
      if (result.isConfirmed) {
        const $modal = $('#procedureEditModal')

        // Handler for when modal is fully hidden
        const handleHidden = () => {
          // Unbind the event listener so it doesn't fire again
          $modal.off('hidden.bs.modal', handleHidden)

          // Prepare data for new insertion
          // We use -1 as procId (insertion id) to indicate new insertion
          // But we need the procedure code (e.g. "TEST") passed as procedureCode
          const no = `${teethPosition}_${procedureCode}`

          // Call setInsuredProcedure to open modal for new insertion
          // We pass empty history and oldProc as it's a new one
          // Pass defaultPrice as initial doctorCost for auto-fill
          // Pass today's date from server for insertionDate, then isAttached
          const todayDate = gon.today_date || ''
          console.log('Calling setInsuredProcedure with:', { no, coverageRate: validCoverageRate, defaultPrice: validDefaultPrice, isAttached, todayDate })
          this.setInsuredProcedure(no, -1, labels, [], "", validDefaultPrice, "", validCoverageRate, validDefaultPrice, todayDate, isAttached, null, null, isRadiology)
        }

        if ($modal.hasClass('show')) {
          // Existing repeat flows start inside this modal, so wait until it closes.
          $modal.on('hidden.bs.modal', handleHidden)
          $modal.modal('hide')
        } else {
          // Completed radiology results start directly from SweetAlert; there is no
          // existing procedure modal to close before opening the clean request form.
          handleHidden()
        }
      }
    })
  }

  isRadiologyProcedure(procedureCode) {
    return Array.from(this.element.querySelectorAll('.procedure-item')).some((item) =>
      item.dataset.procedureCode === String(procedureCode) && item.dataset.isRadiology === 'true'
    )
  }

  fetchAndLoadHistory(procedureCode, teethPosition) {
    const historyLoading = $('#procedure_history_loading')
    const historyEmpty = $('#procedure_history_empty')

    historyLoading.show()
    historyEmpty.hide()

    $.ajax({
      type: "get",
      url: "/admin/procedures/get/history",
      dataType: "json",
      beforeSend: (xhr) => { xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content')) },
      data: {
        insured_number: gon.insured_number,
        procedure_code: procedureCode,
        teeth_position: teethPosition,
        doctor_id: gon.doctor_id,
        contract_code: gon.contract_code,
        locale: gon.locale
      },
      success: (result) => {
        if (result.success) {
          this.loadProcedureHistory(procedureCode, teethPosition, result.history || [])
        } else {
          historyLoading.hide()
          historyEmpty.show()
        }
      },
      error: () => {
        historyLoading.hide()
        historyEmpty.show()
      }
    })
  }

  loadProcedureHistory(procedureCode, teethPosition, historyData) {
    const historyContainer = $('#procedure_history_data')
    const historyLoading = $('#procedure_history_loading')
    const historyEmpty = $('#procedure_history_empty')

    // Hide empty message
    historyEmpty.hide()
    historyLoading.hide()

    if (historyData && historyData.length > 0) {
      // Build history cards
      let historyHtml = '<div class="row">'
      historyData.forEach(item => {
        const approvalStatusText = item.approval_status ? (gon.locale === 'ar' ? 'موافق عليه' : 'Approved') : (gon.locale === 'ar' ? 'غير موافق عليه' : 'Not Approved');
        const approvalStatusBadgeClass = item.approval_status ? 'badge-success' : 'badge-warning';
        const doctorApprovalStatusText = item.doctor_approval_status ? (gon.locale === 'ar' ? 'موافق عليه' : 'Approved') : (gon.locale === 'ar' ? 'قيد الانتظار' : 'Pending');

        historyHtml += `
          <div class="col-12 col-sm-6 mb-2">
            <div class="card border h-100">
              <div class="card-body d-flex align-items-center p-2">
                <div class="${gon.locale === 'ar' ? 'ml-2' : 'mr-2'}">
                   <img src="${item.tooth_image_path}" class="img-fluid" style="height: 45px; width: 45px;" alt="Tooth">
                </div>
                <div class="w-100">
                  <div class="d-flex justify-content-between align-items-start mb-1">
                    <h5 class="card-title mb-0" style="font-size: 0.875rem; line-height: 1.2;">${item.procedure_name || '---'}</h5>
                    <span class="badge ${approvalStatusBadgeClass}" style="font-size: 0.7rem; padding: 0.15rem 0.4rem;">${approvalStatusText}</span>
                  </div>
                  
                  <p class="card-text text-muted mb-1" style="font-size: 0.75rem;">
                    <i class="fe fe-user ${gon.locale === 'ar' ? 'ml-1' : 'mr-1'}" style="font-size: 0.7rem;"></i> ${item.doctor_name}
                  </p>
                  
                  <p class="card-text text-muted mb-1" style="font-size: 0.75rem;">
                    <i class="fe fe-calendar ${gon.locale === 'ar' ? 'ml-1' : 'mr-1'}" style="font-size: 0.7rem;"></i> ${item.insertion_date}
                  </p>
                  <div class="small border-top pt-1">
                    <div class="d-flex justify-content-between"><span>${gon.locale === 'ar' ? 'إجمالي المطالبة' : 'Gross claim'}</span><strong>${Number(item.estimate_cost || 0).toFixed(2)}</strong></div>
                    <div class="d-flex justify-content-between"><span>${gon.locale === 'ar' ? 'حصة المريض' : 'Patient share'} (${Number(item.patient_share_rate || 0).toFixed(2)}%)</span><strong>${Number(item.patient_share || 0).toFixed(2)}</strong></div>
                    <div class="d-flex justify-content-between"><span>${gon.locale === 'ar' ? 'حصة التأمين قبل الخصومات' : 'Insurance share before deductions'}</span><strong>${Number(item.insurance_share || 0).toFixed(2)}</strong></div>
                    <div class="d-flex justify-content-between text-success"><span>${gon.locale === 'ar' ? 'مبلغ الدفع بعد الخصومات' : 'Payment amount after deductions'}</span><strong>${Number(item.payment_amount || 0).toFixed(2)}</strong></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `
      })
      historyHtml += '</div>'

      historyContainer.html(historyHtml).show()
      historyLoading.hide()
    } else {
      historyContainer.hide()
      historyLoading.hide()
      historyEmpty.show()
    }
  }

  buildHistoryListHtml(oldProc, history) {
    let items = []
    if (Array.isArray(oldProc)) {
      if (Array.isArray(oldProc[0])) {
        items = oldProc[0]
      } else {
        items = oldProc
      }
    }
    if (!items || items.length === 0 || items[0] === '' || items[0] === undefined) {
      items = ['---']
    }
    const hist = history ? history : ''
    return hist + '<br/>' + `<ul>${items.map(item => `<li>${item}</li>`).join('')}</ul>`
  }

  deleteConfirmedProcedure(event) {
    event.preventDefault()
    const data = event.params

    console.log('deleteConfirmedProcedure data:', data)

    // Check if procedure requires images
    const requiresImages = (data.isAttached === true || data.isAttached === 'true')

    const labels = {
      date: data.dateLbl,
      doctorCost: data.doctorCostLbl,
      estimateCost: data.estimateCostLbl
    }

    // Function to open modal with history data
    const openModalWithHistory = (historyData, showHistoryTab) => {
      // For doctors, always show as read-only with "Add New Insertion" button
      const isDoctor = gon.user_type === 'doctor'

      if (!requiresImages) {
        // Procedure doesn't require images - show modal without images
        this.showProcedureModal({
          dateLabel: labels.date,
          doctorCostLabel: labels.doctorCost,
          estimateCostLabel: labels.estimateCost,
          insurtionDate: data.insertionDate,
          doctorCost: data.doctorCost,
          estimateCost: data.estimateCost,
          imagesHtml: '',
          coverageRate: data.coverageRate,
          defaultPrice: data.defaultPrice,
          isBlocked: data.isBlocked || false,
          disableAllFields: isDoctor,
          showSaveButton: !isDoctor,
          procedureCode: data.procId,
          teethPosition: data.teethPos,
          showAddNewButton: true,
          isAttached: requiresImages,
          showHistoryTab: showHistoryTab,
          historyData: historyData,
          insertionId: data.insurtionId,
          onSave: (result) => {
            this.isSaving = false
            this.updateInsuredProcedure(data.insurtionId, result, `${data.teethPos}_${data.procId}`)
          }
        })
      } else {
        // Procedure requires images - fetch and show images
        // Construct the combined format "teethPos_procCode" that getProcedureInsurtionImages expects
        const combinedNo = `${data.teethPos}_${data.procId}`
        this.getProcedureInsurtionImages(combinedNo, data.insurtionId, labels,
          data.insertionDate, data.doctorCost, data.estimateCost, data.coverageRate, data.defaultPrice, data.isBlocked || false, requiresImages, showHistoryTab, historyData)
      }
    }

    // Fetch history if procedure is blocked (or just fetch it anyway to check)
    // We reuse the logic from checkAndShowHistoryTab but adapted
    $.ajax({
      type: "get",
      url: "/admin/procedures/get/history",
      data: {
        insured_number: gon.insured_number,
        procedure_code: data.procId,
        teeth_position: data.teethPos,
        doctor_id: gon.doctor_id,
        contract_code: gon.contract_code,
        locale: gon.locale
      },
      success: (response) => {
        if (response.success) {
          openModalWithHistory(response.history, response.show_history_tab)
        } else {
          openModalWithHistory([], false)
        }
      },
      error: () => {
        openModalWithHistory([], false)
      }
    })
  }

  procApproval(event) {
    event.preventDefault()
    const data = event.params
    const {
      no, procId, status, approvalId, isAttached,
      coverageRate, defaultPrice, isBlocked,
      dateLbl, doctorCostLbl, estimateCostLbl,
      insertionDate, doctorCost, estimateCost
    } = data

    console.log('procApproval called with FULL data:', data)
    console.log('procApproval extracted:', { no, procId, status, approvalId, isAttached, coverageRate, defaultPrice, isBlocked, user_type: gon.user_type })

    // Existing insertions use the same details modal for doctors and admins.
    // The modal config below gives admins a direct "Add New Insertion" action.
    // NOTE: `procId` may come as number (-1) or string ("-1") from Stimulus params.
    const hasExistingInsertionId = procId !== null && procId !== undefined && String(procId) !== '-1'

    if (hasExistingInsertionId) {
      // Check if procedure is blocked by fetching procedure insertion details
      $.ajax({
        type: "get",
        url: "/admin/procedures/get/uploaded_images",
        dataType: "json",
        beforeSend: (xhr) => { xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content')) },
        data: {
          procedure_id: procId,
          doctor_id: gon.doctor_id,
          locale: gon.locale
        },
        success: (result) => {
          if (result.success && result.is_blocked === false) {
            // Procedure is NOT blocked - directly show edit modal
            const idsData = no.split("_")
            const teethPos = idsData[0]
            const procCode = idsData[1]

            const labels = {
              date: dateLbl || gon.insertion_date || 'Date',
              doctorCost: doctorCostLbl || gon.one_teeth_require || 'Doctor Cost',
              estimateCost: estimateCostLbl || gon.estimate_cost || 'Estimate Cost'
            }

            // Check if procedure requires images
            const requiresImages = (isAttached === true || isAttached === 'true')

            // Check if we should show history tab for this procedure
            this.checkAndShowHistoryTab(procCode, teethPos, (showHistoryTab, historyData) => {
              const actualCoverageRate = parseFloat(coverageRate) || 0
              const actualDefaultPrice = parseFloat(defaultPrice) || 0

              console.log('Blocked procedure - Using values:', { actualCoverageRate, actualDefaultPrice, resultDoctorCost: result.doctor_cost })

              if (!requiresImages) {
                // No images required - show modal without images, all fields disabled
                this.showProcedureModal({
                  dateLabel: labels.date,
                  doctorCostLabel: labels.doctorCost,
                  estimateCostLabel: labels.estimateCost,
                  insurtionDate: result.insurtion_date || insertionDate || '',
                  doctorCost: result.doctor_cost || doctorCost || '',
                  estimateCost: result.estimate_cost || estimateCost || '',
                  imagesHtml: '',
                  coverageRate: actualCoverageRate,
                  defaultPrice: actualDefaultPrice,
                  isBlocked: false,
                  disableAllFields: true,
                  allowImagesOnly: false,
                  showSaveButton: false,
                  showAddNewButton: true,
                  isAttached: requiresImages,
                  showHistoryTab: showHistoryTab,
                  procedureCode: procCode,
                  teethPosition: teethPos,
                  historyData: historyData,
                  insertionId: procId,
                  onSave: () => { } // No save for read-only
                })
              } else {
                // Images required - show modal with all fields disabled except image upload if creator matches
                const combinedNo = `${teethPos}_${procCode}`
                this.getProcedureInsurtionImages(combinedNo, procId, labels,
                  result.insurtion_date || insertionDate || '', result.doctor_cost || doctorCost || '', result.estimate_cost || estimateCost || '', actualCoverageRate, actualDefaultPrice, false, requiresImages, showHistoryTab, historyData)
              }
            })
          } else if (result.success && result.is_blocked === true) {
            // Procedure IS blocked - show modal with fields disabled (except image upload if creator matches)
            const idsData = no.split("_")
            const teethPos = idsData[0]
            const procCode = idsData[1]

            const labels = {
              date: gon.insertion_date || 'Date',
              doctorCost: gon.one_teeth_require || 'Doctor Cost',
              estimateCost: gon.estimate_cost || 'Estimate Cost'
            }

            // Check if current doctor is the creator
            const isCreator = result.doctor_id && gon.doctor_id && result.doctor_id === gon.doctor_id

            // Check if procedure requires images
            const requiresImages = (isAttached === true || isAttached === 'true')

            // Check if we should show history tab for this procedure
            this.checkAndShowHistoryTab(procCode, teethPos, (showHistoryTab, historyData) => {
              const actualCoverageRate = parseFloat(coverageRate) || 0
              const actualDefaultPrice = parseFloat(defaultPrice) || 0

              console.log('Blocked procedure - Using values:', { actualCoverageRate, actualDefaultPrice, resultDoctorCost: result.doctor_cost })

              if (!requiresImages) {
                const adminCanEditDate = gon.user_type === 'admin'
                // No images required - show modal without images, all fields disabled
                this.showProcedureModal({
                  dateLabel: labels.date,
                  doctorCostLabel: labels.doctorCost,
                  estimateCostLabel: labels.estimateCost,
                  insurtionDate: result.insurtion_date || insertionDate || '',
                  doctorCost: result.doctor_cost || doctorCost || '',
                  estimateCost: result.estimate_cost || estimateCost || '',
                  imagesHtml: '',
                  coverageRate: actualCoverageRate,
                  defaultPrice: actualDefaultPrice,
                  isBlocked: true,
                  disableAllFields: !adminCanEditDate,
                  allowImagesOnly: false,
                  showSaveButton: adminCanEditDate,
                  showAddNewButton: true,
                  showHistoryTab: showHistoryTab,
                  procedureCode: procCode,
                  teethPosition: teethPos,
                  historyData: historyData,
                  insertionId: procId,
                  isAttached: requiresImages,
                  onSave: (resultdata) => {
                    if (adminCanEditDate) this.updateInsuredProcedure(procId, resultdata, no)
                  }
                })
              } else {
                // Images required - show modal with all fields disabled except image upload if creator matches
                const combinedNo = `${teethPos}_${procCode}`
                this.getProcedureInsurtionImages(combinedNo, procId, labels,
                  result.insurtion_date || insertionDate || '', result.doctor_cost || doctorCost || '', result.estimate_cost || estimateCost || '', actualCoverageRate, actualDefaultPrice, true, requiresImages, showHistoryTab, historyData)
              }
            })
          } else {
            // Error or other case - show normal approval flow
            this.showApprovalFlow(status, no, procId, isAttached, approvalId, data)
          }
        },
        error: () => {
          // Error fetching details - show normal approval flow
          this.showApprovalFlow(status, no, procId, isAttached, approvalId, data)
        }
      })
    } else {
      // Not a doctor or no valid procId - show normal approval flow
      this.showApprovalFlow(status, no, procId, isAttached, approvalId, data)
    }
  }

  showApprovalFlow(status, no, procId, isAttached, approvalId, procedureData = {}) {
    if (gon.user_type === 'admin') {
      const idsData = no.split("_")
      this.addNewInsertion(idsData[1], idsData[0], {
        date: procedureData.dateLbl || gon.insertion_date,
        doctorCost: procedureData.doctorCostLbl || gon.one_teeth_require,
        estimateCost: procedureData.estimateCostLbl || gon.estimate_cost
      }, procedureData.coverageRate, procedureData.defaultPrice, true, isAttached, this.isRadiologyProcedure(idsData[1]))
      return
    }

    if (status) {
      const idsData = no.split("_")
      const teethPos = idsData[0]
      const procCode = idsData[1]

      // First check if there's already an approval for this tooth/procedure
      this.fetchApprovalStatus(procCode, teethPos, (approval) => {
        if (!approval) {
          // No approval exists - show request approval dialog
          Swal.fire({
            title: gon.approval_new_title,
            html: gon.approval_new_msg,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            confirmButtonText: gon.approval_new_confirm,
            cancelButtonText: gon.approval_new_cancel
          }).then((result) => {
            if (result.isConfirmed) {
              this.sendProceduralApproval(procCode, teethPos)
            }
          })
        } else if (approval.approval_status === false && approval.is_proceeded === false) {
          // Pending approval - show waiting message
          Swal.fire({
            title: gon.approval_new_title || (gon.locale === 'ar' ? 'يتطلب الحصول على موافقة' : 'Approval Required'),
            html: gon.locale === 'ar' ? 'طلبك قيد المراجعة. يرجى انتظار الموافقة.' : 'Your request is under review. Please wait for approval.',
            icon: 'info',
            showCancelButton: false,
            confirmButtonColor: '#6c757d',
            confirmButtonText: gon.locale === 'ar' ? 'في انتظار الموافقة' : 'Waiting for Approval',
            allowOutsideClick: true
          })
        } else if (approval.approval_status === false && approval.is_proceeded === true) {
          // Rejected - show rejected message
          Swal.fire({
            title: gon.locale === 'ar' ? 'تم رفض الطلب' : 'Request Rejected',
            html: gon.locale === 'ar' ? 'تم رفض طلب الموافقة الخاص بك.' : 'Your approval request has been rejected.',
            icon: 'error',
            showCancelButton: false,
            confirmButtonColor: '#d33',
            confirmButtonText: gon.locale === 'ar' ? 'حسناً' : 'OK'
          })
        } else if (approval.approval_status === true && approval.is_proceeded === false) {
          // Approved but not yet used - show approved message with option to add
          Swal.fire({
            title: gon.locale === 'ar' ? 'تمت الموافقة' : 'Approved',
            html: gon.locale === 'ar' ? 'تمت الموافقة على طلبك. يمكنك إضافة الإجراء الآن.' : 'Your request has been approved. You can add the procedure now.',
            icon: 'success',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: gon.locale === 'ar' ? 'إضافة الإجراء' : 'Add Procedure',
            cancelButtonText: gon.locale === 'ar' ? 'إلغاء' : 'Cancel'
          }).then((result) => {
            if (result.isConfirmed) {
              // Trigger the add procedure flow
              this.addNewInsertion(procCode, teethPos, {
                date: gon.insertion_date || 'Date',
                doctorCost: gon.one_teeth_require || 'Doctor Cost',
                estimateCost: gon.estimate_cost || 'Estimate Cost'
              }, 0, 0, true, isAttached, this.isRadiologyProcedure(procCode))
            }
          })
        } else if (approval.approval_status === true && approval.is_proceeded === true) {
          // Approval was used - need to request a new one
          Swal.fire({
            title: gon.approval_new_title,
            html: gon.approval_new_msg,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            confirmButtonText: gon.approval_new_confirm,
            cancelButtonText: gon.approval_new_cancel
          }).then((result) => {
            if (result.isConfirmed) {
              this.sendProceduralApproval(procCode, teethPos)
            }
          })
        }
      })
    } else {
      this.checkApprovalStatus(no, procId, isAttached, approvalId)
    }
  }

  sendProceduralApproval(procCode, teethPos) {
    const data = {
      insured_number: gon.insured_number,
      doctor_id: gon.doctor_id,
      procedure_code: procCode,
      teeth_position: teethPos
    }

    $.ajax({
      type: "post",
      url: "/admin/procedures/approval/add",
      dataType: "json",
      beforeSend: (xhr) => { xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content')) },
      data: data,
      success: (result) => {
        if (result.success) {
          const link = document.querySelector(`.link_${teethPos}_${procCode}`)
          if (link) {
            link.dataset.insertionProcIdParam = -1
            link.dataset.insertionStatusParam = false
            link.dataset.insertionApprovalIdParam = result.id

            const lock = document.querySelector(`.lock-proc-${teethPos}_${procCode}`)
            if (lock) this.renderToothStatusMarker(lock, 'waiting')
          }

          Swal.fire({
            position: 'top-end',
            icon: 'success',
            title: result.msg,
            showConfirmButton: false,
            timer: 1800
          })
        }
      }
    })
  }

  openRadiologyProcedureRequest(requestedForProcedureCode, teethPosition) {
    const procedureDate = document.getElementById('procedure_modal_insurtion_date')?.value || ''
    const minimumProcedureDate = gon.contract_start_date || ''
    const maximumProcedureDate = gon.contract_end_date || ''
    if (!procedureDate) {
      Swal.fire({ icon: 'error', text: gon.invalid_procedure_date })
      return
    }
    if ((minimumProcedureDate && procedureDate < minimumProcedureDate) ||
        (maximumProcedureDate && procedureDate > maximumProcedureDate)) {
      Swal.fire({ icon: 'error', text: gon.date_outside_contract })
      return
    }

    this.openRadiologyProcedureSelection(requestedForProcedureCode, teethPosition, 'external_provider', procedureDate)
  }

  openRadiologyProcedureSelection(requestedForProcedureCode, teethPosition, fulfillmentMode, procedureDate) {
    const procedures = Array.isArray(gon.radiology_procedures) ? gon.radiology_procedures : []
    const options = procedures.map((procedure) =>
      `<option value="${this.escapeHtml(procedure.code)}">${this.escapeHtml(procedure.name)} (${this.escapeHtml(procedure.code)})</option>`
    ).join('')

    if (procedures.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: gon.locale === 'ar' ? 'لا توجد إجراءات أشعة' : 'No radiology procedures available',
        text: gon.locale === 'ar' ? 'لا توجد إجراءات أشعة مفعلة لهذا الطبيب والعقد.' : 'No active radiology procedures are assigned to this doctor and contract.'
      })
      return
    }

    Swal.fire({
      title: gon.locale === 'ar' ? 'طلب موافقة أشعة' : 'Request radiology approval',
      html: `
        <input id="radiology_procedure_search" type="search" class="swal2-input" placeholder="${gon.locale === 'ar' ? 'ابحث بالاسم أو الرمز' : 'Search by name or code'}">
        <select id="radiology_procedure_select" class="swal2-select" size="6" style="display:block;width:90%;margin:1rem auto;">${options}</select>
      `,
      showCancelButton: true,
      confirmButtonText: gon.locale === 'ar' ? 'إرسال الطلب' : 'Send request',
      cancelButtonText: gon.cancel,
      didOpen: () => {
        const search = document.getElementById('radiology_procedure_search')
        const select = document.getElementById('radiology_procedure_select')
        search.addEventListener('input', () => {
          const query = search.value.trim().toLocaleLowerCase()
          Array.from(select.options).forEach((option) => {
            option.hidden = query.length > 0 && !option.text.toLocaleLowerCase().includes(query)
          })
          const firstVisible = Array.from(select.options).find((option) => !option.hidden)
          if (firstVisible) select.value = firstVisible.value
        })
      },
      preConfirm: () => {
        const procedureCode = document.getElementById('radiology_procedure_select').value
        if (!procedureCode) {
          Swal.showValidationMessage(gon.locale === 'ar' ? 'اختر إجراء أشعة' : 'Choose a radiology procedure')
          return false
        }
        return procedureCode
      }
    }).then((result) => {
      if (!result.isConfirmed) return
      this.sendRadiologyApproval(result.value, requestedForProcedureCode, teethPosition, fulfillmentMode, procedureDate)
    })
  }

  sendRadiologyApproval(radiologyProcedureCode, requestedForProcedureCode, teethPosition, fulfillmentMode = 'external_provider', procedureDate = '') {
    $.ajax({
      type: 'post',
      url: '/admin/procedures/approval/add',
      dataType: 'json',
      beforeSend: (xhr) => xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content')),
      data: {
        insured_number: gon.insured_number,
        doctor_id: gon.doctor_id,
        procedure_code: radiologyProcedureCode,
        requested_for_procedure_code: requestedForProcedureCode,
        radiology_fulfillment_mode: fulfillmentMode,
        teeth_position: teethPosition,
        insurtion_date: procedureDate,
        contract_code: gon.contract_code,
        locale: gon.locale
      },
      success: (response) => {
        if (response.success) {
          const radiology = (gon.radiology_procedures || []).find((procedure) => procedure.code === radiologyProcedureCode)
          this.applyLinkedRadiologyStatus({
            requestedForProcedureCode: requestedForProcedureCode,
            teethPosition: teethPosition,
            approvalId: response.id,
            radiologyProcedureCode: radiologyProcedureCode,
            radiologyProcedureName: radiology?.name || radiologyProcedureCode,
            radiologyFulfillmentMode: response.radiology_fulfillment_mode,
            status: 'pending',
            statusLabel: gon.locale === 'ar' ? 'طلب الموافقة على الأشعة قيد الانتظار' : 'Radiology approval is pending',
            blocksTreatment: true,
            resultViewUrl: '',
            ticketNumber: '',
            ticketPrintUrl: '',
            ticketStartUrl: ''
          })

          // Keep the treatment insertion modal usable while authorization and
          // ticket creation continue in the background. Its radiology section
          // is refreshed in place with the current state and ticket details.
          this.authorizationDialogUrl = null
          this.authorizationDialogState = null
          if (Swal.isVisible()) Swal.close()
          this.refreshOpenRadiologyModal()
          return
        }

        Swal.fire({
          icon: 'error',
          title: gon.locale === 'ar' ? 'تعذر إرسال الطلب' : 'Unable to send request',
          text: response.error
        })
      },
      error: (xhr) => Swal.fire({
        icon: 'error',
        title: gon.locale === 'ar' ? 'تعذر إرسال الطلب' : 'Unable to send request',
        text: xhr.responseJSON?.error || (gon.locale === 'ar' ? 'حدث خطأ أثناء إرسال الطلب' : 'An error occurred while sending the request')
      })
    })
  }

  renderLinkedRadiologyTicket(ticketNumber, ticketPrintUrl, status = '', ticketStartUrl = '') {
    const hasTicket = status !== 'results_available' && !!ticketNumber
    $('#procedure_modal_radiology_ticket_number').text(ticketNumber || '')
    $('#procedure_modal_radiology_ticket_print_btn')
      .attr('href', hasTicket && ticketPrintUrl ? ticketPrintUrl : '#')
      .toggle(hasTicket && !!ticketPrintUrl)
    $('#procedure_modal_radiology_ticket_start_btn')
      .attr('href', hasTicket && ticketStartUrl ? ticketStartUrl : '#')
      .toggle(hasTicket && !!ticketStartUrl)
    $('#procedure_modal_radiology_ticket').toggle(hasTicket)
  }

  applyLinkedRadiologyStatus(status) {
    const linkClass = `link_${status.teethPosition}_${status.requestedForProcedureCode}`
    const links = Array.from(document.querySelectorAll('a')).filter((link) => link.classList.contains(linkClass))
    const item = links.find((link) => link.offsetParent !== null)?.closest('.item') || links[0]?.closest('.item')
    if (!item) return

    item.querySelector('.linked-radiology-tooth-status')?.remove()
    item.querySelector('.linked-radiology-status-data')?.remove()

    if (!item.querySelector('.tooth-authorization-marker')) {
      const marker = document.createElement('span')
      marker.className = `linked-radiology-tooth-status linked-radiology-tooth-status-${status.status} doctor-tooth-status-marker`
      marker.setAttribute('role', 'status')
      marker.setAttribute('aria-label', status.statusLabel)
      marker.title = status.statusLabel
      const dotStatus = status.status === 'results_available'
        ? 'results'
        : (status.status === 'approved'
            ? 'authorized'
            : (['rejected', 'failed'].includes(status.status)
                ? 'failed'
                : (status.status === 'radiology_in_progress' ? 'radiology' : 'waiting')))
      marker.innerHTML = this.toothStatusDot(dotStatus)
      item.appendChild(marker)
    }

    const data = document.createElement('span')
    data.className = 'linked-radiology-status-data'
    data.hidden = true
    data.dataset.procedureCode = String(status.requestedForProcedureCode)
    data.dataset.toothPosition = String(status.teethPosition)
    data.dataset.approvalId = String(status.approvalId)
    data.dataset.radiologyProcedureCode = status.radiologyProcedureCode
    data.dataset.radiologyProcedureName = status.radiologyProcedureName
    data.dataset.status = status.status
    data.dataset.statusLabel = status.statusLabel
    data.dataset.blocksTreatment = String(status.blocksTreatment === true)
    data.dataset.resultViewUrl = status.resultViewUrl || ''
    data.dataset.ticketNumber = status.ticketNumber || ''
    data.dataset.ticketPrintUrl = status.ticketPrintUrl || ''
    data.dataset.ticketStartUrl = status.ticketStartUrl || ''
    item.appendChild(data)

    $('#procedure_modal_radiology_message').html(
      `<strong>${this.escapeHtml(status.statusLabel)}</strong><br>${this.escapeHtml(status.radiologyProcedureName)} (${this.escapeHtml(status.radiologyProcedureCode)})`
    )
    $('#procedure_modal_radiology_request_btn').hide()
    const saveButton = $('#procedure_modal_save_btn')
    if (status.blocksTreatment) {
      saveButton
        .prop('disabled', true)
        .addClass('btn-secondary')
        .removeClass('btn-primary')
        .text(gon.locale === 'ar' ? 'بانتظار نتائج الأشعة' : 'Waiting for Radiology Results')
    } else {
      saveButton
        .prop('disabled', false)
        .removeClass('btn-secondary')
        .addClass('btn-primary')
        .text(gon.save_label || 'Save')
    }
    $('#procedure_modal_radiology_results_btn')
      .toggle(status.status === 'results_available' && !!status.resultViewUrl)
      .off('click')
      .on('click', () => this.showSubmittedRadiologyResult(status.resultViewUrl, gon.locale === 'ar'))
    this.renderLinkedRadiologyTicket(status.ticketNumber, status.ticketPrintUrl, status.status, status.ticketStartUrl)
  }

  refreshOpenRadiologyModal() {
    if (this.radiologyModalRefreshTimer) window.clearTimeout(this.radiologyModalRefreshTimer)
    this.radiologyModalRefreshTimer = null
    this.refreshOpenRadiologyModalAttempt(0)
  }

  refreshOpenRadiologyModalAttempt(attempt) {
    if (!$('#procedureEditModal').hasClass('show')) return

    const statusElement = Array.from(document.querySelectorAll('.linked-radiology-status-data')).find((element) =>
      element.dataset.procedureCode === this.openProcedureCode &&
      element.dataset.toothPosition === this.openTeethPosition
    )
    if (!statusElement) return

    this.refreshLinkedRadiologyStatus(statusElement, (response) => {
      const ticketReady = Boolean(response?.radiology_ticket_number)
      const terminal = ['results_available', 'rejected', 'failed'].includes(response?.linked_radiology_state)
      if ((ticketReady || terminal) && this.isPendingWorkflowState(this.authorizationDialogState)) {
        this.authorizationDialogState = null
        this.authorizationDialogUrl = null
        Swal.close()
      }
      if (ticketReady || terminal || !$('#procedureEditModal').hasClass('show')) return

      this.radiologyModalRefreshTimer = window.setTimeout(() => {
        this.refreshOpenRadiologyModalAttempt(attempt + 1)
      }, 2000)
    })
  }

  refreshLinkedRadiologyStatus(statusElement, onComplete = null) {
    $.ajax({
      type: 'get',
      url: '/admin/procedures/approval/check',
      dataType: 'json',
      data: { approval_id: statusElement.dataset.approvalId, locale: gon.locale },
      success: (response) => {
        if (!response.success || !response.linked_radiology_state) {
          if (typeof onComplete === 'function') onComplete(null)
          return
        }
        this.applyLinkedRadiologyStatus({
          requestedForProcedureCode: response.requested_for_procedure_code,
          teethPosition: response.teeth_position,
          approvalId: statusElement.dataset.approvalId,
          radiologyProcedureCode: response.proc_code,
          radiologyProcedureName: response.radiology_procedure_name,
          status: response.linked_radiology_state,
          statusLabel: response.linked_radiology_status_label,
          blocksTreatment: response.linked_radiology_blocks_treatment === true,
          resultViewUrl: response.radiology_result_view_url || '',
          ticketNumber: response.radiology_ticket_number || '',
          ticketPrintUrl: response.radiology_ticket_print_url || '',
          ticketStartUrl: response.radiology_ticket_start_url || ''
        })
        if (typeof onComplete === 'function') onComplete(response)
      },
      error: () => {
        if (typeof onComplete === 'function') onComplete(null)
      }
    })
  }

  addProcedureToCarousel(procedureCardHtml) {
    // Check if we have the old procedures carousel target
    if (!this.hasOldProceduresCarouselTarget) return

    // Hide "no data" message if it exists
    if (this.hasNoProceduresMessageTarget) {
      this.noProceduresMessageTarget.style.display = 'none'
    }

    const carousel = this.oldProceduresCarouselTarget

    // Check if owl carousel is initialized
    const owlData = $(carousel).data('owl.carousel')

    if (owlData) {
      // Owl carousel is initialized - add item dynamically
      $(carousel).trigger('add.owl.carousel', [$(procedureCardHtml)])
        .trigger('refresh.owl.carousel')
        .trigger('to.owl.carousel', [0, 300]) // Navigate to first item (newest)
    } else {
      // Owl carousel not initialized yet - just append the HTML
      carousel.insertAdjacentHTML('afterbegin', procedureCardHtml)
    }
  }

  updateProcedureInCarousel(procId, procedureCardHtml) {
    // Check if we have the old procedures carousel target
    if (!this.hasOldProceduresCarouselTarget) return

    const carousel = this.oldProceduresCarouselTarget

    // Check if owl carousel is initialized
    const owlData = $(carousel).data('owl.carousel')

    if (owlData) {
      // Owl carousel is initialized
      // Find the item containing this procedure (by looking for the link with proc_ins_id in URL)
      const $items = $(carousel).find('.owl-item')
      let foundIndex = -1

      $items.each(function (index, item) {
        const $link = $(item).find('a[href*="proc_ins_id=' + procId + '"]')
        if ($link.length > 0) {
          foundIndex = index
          return false // Break the loop
        }
      })

      if (foundIndex >= 0) {
        // Found the item - remove it and add the new one at the same position
        $(carousel).trigger('remove.owl.carousel', [foundIndex])
          .trigger('add.owl.carousel', [$(procedureCardHtml), foundIndex])
          .trigger('refresh.owl.carousel')
          .trigger('to.owl.carousel', [foundIndex, 300]) // Stay at the same position
      } else {
        // Item not found - add it as new (prepend to start)
        $(carousel).trigger('add.owl.carousel', [$(procedureCardHtml)])
          .trigger('refresh.owl.carousel')
          .trigger('to.owl.carousel', [0, 300])
      }
    } else {
      // Owl carousel not initialized - try to find and replace in DOM
      const $carousel = $(carousel)
      const $existingCard = $carousel.find('a[href*="proc_ins_id=' + procId + '"]').closest('.item')

      if ($existingCard.length > 0) {
        $existingCard.replaceWith(procedureCardHtml)
      } else {
        // Not found - add as new
        carousel.insertAdjacentHTML('afterbegin', procedureCardHtml)
      }
    }

    // Hide "no data" message if it exists
    if (this.hasNoProceduresMessageTarget) {
      this.noProceduresMessageTarget.style.display = 'none'
    }
  }

  fetchApprovalStatus(procedureCode, teethPosition, callback) {
    // Fetch approval status for a given procedure and tooth position
    $.ajax({
      type: "get",
      url: "/admin/procedures/approval/check_by_position",
      dataType: "json",
      beforeSend: (xhr) => { xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content')) },
      data: {
        insured_number: gon.insured_number,
        doctor_id: gon.doctor_id,
        procedure_code: procedureCode,
        teeth_position: teethPosition
      },
      success: (result) => {
        if (result.success && result.approval) {
          callback(result.approval)
        } else {
          callback(null)
        }
      },
      error: () => {
        callback(null)
      }
    })
  }

  requestApprovalForNewInsertion(procedureCode, teethPosition, buttonElement) {
    // Show confirmation dialog with the approval message
    Swal.fire({
      title: gon.approval_new_title || (gon.locale === 'ar' ? 'يتطلب الحصول على موافقة' : 'This Procedure needs approval'),
      html: gon.approval_new_msg || (gon.locale === 'ar' ? 'هل تريد طلب موافقة لإضافة إجراء جديد؟' : 'Do you want to request approval to add a new insertion?'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: gon.approval_new_confirm || (gon.locale === 'ar' ? 'نعم، أرسل الطلب' : 'Yes, send request'),
      cancelButtonText: gon.approval_new_cancel || (gon.locale === 'ar' ? 'إلغاء' : 'Cancel')
    }).then((result) => {
      if (result.isConfirmed) {
        // Send approval request
        $.ajax({
          type: "post",
          url: "/admin/procedures/approval/add",
          dataType: "json",
          beforeSend: (xhr) => { xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content')) },
          data: {
            insured_number: gon.insured_number,
            doctor_id: gon.doctor_id,
            procedure_code: procedureCode,
            teeth_position: teethPosition,
            contract_code: gon.contract_code
          },
          success: (response) => {
            if (response.success) {
              // Update button to "Waiting for Approval" state
              $(buttonElement).removeClass('btn-info btn-success')
                .addClass('btn-secondary')
                .prop('disabled', true)
                .text(gon.locale === 'ar' ? 'في انتظار الموافقة' : 'Waiting for Approval')
                .off('click')

              Swal.fire({
                position: 'top-end',
                icon: 'success',
                title: gon.locale === 'ar' ? 'تم إرسال الطلب بنجاح' : 'Request sent successfully',
                showConfirmButton: false,
                timer: 1800
              })
            } else {
              Swal.fire({
                icon: 'error',
                title: gon.locale === 'ar' ? 'خطأ' : 'Error',
                text: gon.locale === 'ar' ? 'فشل إرسال الطلب' : 'Failed to send request'
              })
            }
          },
          error: () => {
            Swal.fire({
              icon: 'error',
              title: gon.locale === 'ar' ? 'خطأ' : 'Error',
              text: gon.locale === 'ar' ? 'حدث خطأ أثناء إرسال الطلب' : 'An error occurred while sending the request'
            })
          }
        })
      }
    })
  }

  checkApprovalStatus(no, procId, isAttached, approvalId) {
    // Get doctor_id - for doctors, use gon.doctor_id or current_doctor.email
    const doctorId = gon.doctor_id || (gon.user_type === 'doctor' ? gon.doctor_id : null)
    console.log('checkApprovalStatus called:', { no, procId, isAttached, approvalId, doctorId, gon_doctor_id: gon.doctor_id, user_type: gon.user_type })

    const data = {
      approval_id: approvalId,
      doctor_id: doctorId
    }
    $.ajax({
      type: "get",
      url: "/admin/procedures/approval/check",
      dataType: "json",
      beforeSend: (xhr) => { xhr.setRequestHeader('X-CSRF-Token', $('meta[name="csrf-token"]').attr('content')) },
      data: data,
      success: (result) => {
        if (result.status && result.admin != null) {
          if (result.is_proceeded && result.is_doctor_approved) {
            const idsData = no.split("_")
            Swal.fire({
              title: gon.approval_new_title,
              html: gon.approval_new_msg,
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#3085d6',
              confirmButtonText: gon.approval_new_confirm,
              confirmButtonText: gon.approval_new_confirm,
              cancelButtonText: gon.approval_new_cancel
            }).then((res) => {
              if (res.isConfirmed) {
                this.sendProceduralApproval(idsData[1], idsData[0])
              }
            })
          } else if (result.is_proceeded) {
            // Trigger selectTeeth
            const link = document.querySelector(`.link_${no}`)
            // We can reuse the selectTeeth method but we need to pass an object that looks like an event or refactor selectTeeth
            // Let's mock the event
            const mockEvent = {
              preventDefault: () => { },
              currentTarget: link,
              params: {
                no: result.teeth_position + "_" + result.proc_code,
                procId: result.proc_id,
                isAttached: isAttached,
                // Fallback labels - assuming they are available globally or passed
                dateLbl: gon.insertion_date,
                estimateCostLbl: gon.estimate_cost,
                cancelLbl: gon.cancel,
                confirmLbl: gon.save_label,
                doctorCostLbl: gon.one_teeth_require,
                errMsg: gon.empty_fields
              }
            }
            this.selectTeeth(mockEvent)
          } else {
            const procCode = result.proc_code
            const teethPos = result.teeth_position
            console.log('Approval approved - showing confirmation dialog', { procCode, teethPos, no, procId })
            Swal.fire({
              icon: 'success',
              title: gon.locale === 'ar' ? 'تمت الموافقة على الإجراء, هل ترغب بعمل الإجراء الآن ؟' : 'The procedure has been approved. Do you want to perform it now?',
              showCancelButton: true,
              cancelButtonText: gon.cancel,
              confirmButtonText: gon.confirm
            }).then((res) => {
              if (res.isConfirmed) {
                console.log('User confirmed - proceeding with procedure insertion')
                const lock = document.querySelector(`.lock-proc-${no}`)
                if (lock) lock.style.display = "none"

                const lockPos = document.querySelector(`.lock-proc-${teethPos}_${procCode}`)
                if (lockPos) this.renderToothStatusMarker(lockPos, 'locked')

                const link = document.querySelector(`.link_${teethPos}_${procCode}`)
                if (link) {
                  const fakeColor = link.querySelector('.color-fake')
                  if (fakeColor) {
                    fakeColor.classList.add("color")
                    fakeColor.classList.remove("color-fake")
                  }
                }
                // Pass proper labels and ensure history check happens
                // Use the combined format "teethPos_procCode" for the id parameter
                const combinedNo = `${teethPos}_${procCode}`
                const labels = {
                  date: gon.insertion_date || 'Date',
                  doctorCost: gon.one_teeth_require || 'Doctor Cost',
                  estimateCost: gon.estimate_cost || 'Estimate Cost'
                }
                // Get coverage rate if available
                const coverageRate = 0 // Will be calculated on server
                console.log('Calling setInsuredProcedure with:', { combinedNo, procId, procCode, teethPos })
                this.setInsuredProcedure(combinedNo, procId, labels, [], "", 0, 0, coverageRate, 0, false)
              }
            })
          }
        } else if (!result.status && result.admin != null) {
          Swal.fire({
            icon: 'error',
            title: gon.locale === 'ar' ? 'تم رفض الإجراء من قبل الشركة' : 'The procedure was rejected by the company',
            showCancelButton: false,
            confirmButtonText: gon.confirm
          })
        } else {
          Swal.fire({
            icon: 'info',
            title: gon.locale === 'ar' ? 'طلب الإجراء قيد المراجعة' : 'The procedure request is under review',
            showCancelButton: false,
            confirmButtonText: gon.confirm
          })
        }
      }
    })
  }
}
